import type { GuideCreateInput, GuideCreateResult, GuideList, GuidePageContent, GuideSaveResult } from "../types";
import type { AdminStatus, Agent, ClaudeSettings, PolarisLaunchListing, PolarisLaunchResolved, PolarisLaunchView, McpAskResult, McpConnection, AgentRepoEntry, AgentEffort, AgentRuntime, AgentRuntimeEntry, AgentRuntimeState, AgentTree, Changelog, CommonFile, DevhubAnalyticsFrictionDetail, DevhubAnalyticsReport, DevhubAiContextReport, DevhubAiGuidanceState, DevhubAiSettingsState, DevhubAiUsageReport, DevhubApiSyncReport, DevhubApiSyncRun, DevhubApiSyncRunSummary, DevhubAsset, DevhubAssetList, DevhubAssetFolderCounts, DevhubAssetFolderKey, DevhubAssetRescanResult, DevhubAssist, DevhubBookmarkReport, DevhubAuditEntry, DevhubBatch, DevhubBatchPage, DevhubComponent, DevhubComponentRegistry, DevhubComponentSummary, DevhubComponentUsage, DevhubConfigStatus, DevhubCorpusInfo, DeletedLinkReportData, DeletedPageRow, DevhubDiscardedDraftRow, DevhubDirectoryEntry, DevhubDraft, DevhubReleaseFeedChoice, DevhubPagePropertiesFile, DevhubPropertyRetiringRow, DevhubSdkReleaseFeedAnswer, DevhubSdkReleaseFeedInfo, DevhubFeedback, DevhubFeedbackAction, DevhubHotlinkReport, DevhubLinkReport, DevhubLiveFeedbackFeed, DevhubNesting, DevhubOrder, DevhubPageBar, DevhubPageContent, DevhubPageEntry, DevhubRedirect, DevhubRedirectSettings, DevhubRedirectTarget, DevhubSdkBranch, DevhubSdkBranchUpdate, DevhubSdkContentFile, DevhubSdkContentPr, DevhubSdkDiff, DevhubSdkEdit, DevhubSdkPage, DevhubSdkPublishBlocker, DevhubSdkPublishPlan, DevhubSdkReviewPr, DevhubSdkReleaseFolder, DevhubSdkSummary, DevhubSdkTree, DevhubSearchEntry, DevhubSectionOwner, DevhubStatusErrorLines, DevhubStatusPeriod, DevhubStatusReport, DevhubTag, DevhubTeamSummary, DevhubUserRole, DevhubVariableCategory, DevhubVariableSummary, DevhubVariableUsage, DevhubVisibilityDrift, DevhubFrictionSignal, DocEntry, GraphEntry, GraphEntryDetail, GraphSummary, HubNotification, HumanPresence, NotificationAction, NotificationBucket, InternalLogEntry, KnowledgeBaseStatus, Me, OmniUserEntry, RepoClone, RepoEntry, RepoReadiness, RepoReadinessScanStatus, Session, SessionUser, SkillDetail, SkillSummary, SystemHealth, Task, TaskStatus, DevhubBrubeckReleaseDetail, DevhubBrubeckReleases, DevhubBrubeckReviewState, DevhubBrubeckSource } from "../types";
import { getToken, getLocalUser, notifyAuthExpired, isOktaConfigured } from "./auth";
import type { PublishRow } from "../devhub/lib/publishLatency";
import type { DevhubPropertyDefinition, DevhubSdkStagedChanges } from "../types";

/** A property as the Properties pane lists it: with how many pages carry each value. */
export type DevhubPropertyWithUsage = DevhubPropertyDefinition & { usage: Record<string, number> };

/** One page's properties as its Details panel reads them. */
export interface DevhubPagePropertiesRead {
  properties: DevhubPropertyDefinition[];
  values: Record<string, string[]>;
  published: Record<string, string[]>;
  /** the batch already holding this version's property change, if any */
  batch: { id: string; name: string } | null;
}

// Base URL of the Omni Hub service. When the api serves the SPA itself (the
// single-container deploy), it injects `window.HUB_API_BASE = ""` so calls are
// same-origin (/api/...). In split/local dev the UI is a separate origin, so we
// fall back to the default hub address.
//
// SECURITY (token exfiltration): the `?api=` override is honored in DEV ONLY.
// Every apiFetch attaches the Okta id token as `Authorization: Bearer …`, so an
// attacker link like `https://host/?api=https://evil.example` would ship the
// victim's token to an attacker-controlled origin. In production builds we ignore the query param
// entirely and force the server-injected base (trusted) or, absent that, the
// current origin — never a value the URL can influence.
//
// The `window.HUB_API_BASE ?? …` uses `??` (not `||`) so an injected empty
// string — meaning "same origin" — is honored rather than treated as falsy.
function resolveApiBase(): string {
  // Server-injected, trusted (may legitimately be "" for same-origin).
  const injected = window.HUB_API_BASE;
  if (!import.meta.env.DEV) {
    return injected ?? location.origin;
  }
  // Dev only: allow the ?api= override so the Vite dev server (separate origin)
  // can point at a local/remote hub (e.g. ?api=http://localhost:3000).
  const override = new URLSearchParams(location.search).get("api");
  return override || (injected ?? "http://localhost:3000");
}

export const API_BASE = resolveApiBase();

const jsonHeaders = { "Content-Type": "application/json" };

// Attach the Okta ID token (when signed in) as a Bearer on every API call. The
// app gates its data routes when Okta is configured, so an unauthenticated call
// gets a 401 — notifyAuthExpired() tells AuthGate to drop back to the sign-in
// screen. We fire it whenever Okta is configured (isOktaConfigured), even if the
// local token was null/already expired: once the id token expires in an open tab
// getToken() returns null, so no Bearer is sent and the 401 comes back with none
// — the user still needs the re-login prompt, not a generic load error. Off-Okta
// (auth unconfigured) there is no token and the gate is open, so a 401 must NOT
// bounce anyone to a sign-in screen.
// Statuses worth trying again: the caller did nothing wrong and the same request
// a moment later can succeed. 429 is the per-IP guardrail (a whole office shares
// one egress IP, so a colleague's page load can spend your budget); 502/503/504
// are the ALB answering while a task drains during a rolling deploy or its event
// loop is busy with a cold corpus scan. Everything else — 400/401/403/404/500 —
// is a real answer and retrying it only delays showing it.
const RETRY_STATUSES = new Set([429, 502, 503, 504]);
const RETRY_ATTEMPTS = 3;
const RETRY_BASE_MS = 300;

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Honour `Retry-After` (seconds, or an HTTP date) when the server sent one, else
 *  exponential backoff with full jitter. Capped so a retry never outlasts the
 *  patience of the person looking at the spinner. */
function retryDelayMs(res: Response | null, attempt: number): number {
  const header = res?.headers.get("retry-after");
  if (header) {
    const seconds = Number(header);
    const ms = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(header) - Date.now();
    if (ms > 0) return Math.min(ms, 5_000);
  }
  return Math.random() * Math.min(5_000, RETRY_BASE_MS * 2 ** attempt);
}

/**
 * Attach the Okta ID token (when signed in) as a Bearer on every API call. The
 * app gates its data routes when Okta is configured, so an unauthenticated call
 * gets a 401 — notifyAuthExpired() tells AuthGate to drop back to the sign-in
 * screen. We fire it whenever Okta is configured (isOktaConfigured), even if the
 * local token was null/already expired: once the id token expires in an open tab
 * getToken() returns null, so no Bearer is sent and the 401 comes back with none
 * — the user still needs the re-login prompt, not a generic load error. Off-Okta
 * (auth unconfigured) there is no token and the gate is open, so a 401 must NOT
 * bounce anyone to a sign-in screen.
 *
 * READS RETRY (ENG-54607). A single transient failure used to be permanent: the
 * DevHub catalog turned one rejected GET into "Failed to load docs" and never
 * asked again, so a 429 or a 502 lasting a second left a dead sidebar until the
 * user reloaded the whole app — which itself costs ~70 more API calls, which is
 * why refreshing only sometimes helped. Retrying is safe here because it is
 * scoped to requests with NO body and no explicit method (i.e. GETs): every
 * mutation in this client passes `method`, so nothing non-idempotent can be
 * replayed. A 401 short-circuits immediately — a lapsed session is not
 * transient, and re-asking would just fire more unauthenticated calls.
 */
async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers ?? {});
  const token = getToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  } else {
    // Off-Okta: carry the default Omni Hub sign-in identity so the server resolves
    // the same person (user box + presence). Encoded — display names may be non-ASCII.
    const local = getLocalUser();
    if (local) {
      headers.set("X-Omni-User", encodeURIComponent(local.name));
      if (local.email) headers.set("X-Omni-Email", encodeURIComponent(local.email));
    }
  }
  const retryable = !init.method && init.body == null;
  const attempts = retryable ? RETRY_ATTEMPTS : 1;

  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) await sleep(retryDelayMs(lastError instanceof Response ? lastError : null, attempt - 1));
    let res: Response;
    try {
      res = await fetch(input, { ...init, headers });
    } catch (err) {
      // A rejected fetch is a dropped connection / DNS blip / offline tab — the
      // most transient failure of all, and the one that reaches JS with no status.
      lastError = err;
      if (attempt === attempts - 1) throw err;
      continue;
    }
    if (res.status === 401 && (token || isOktaConfigured())) {
      notifyAuthExpired();
      return res;
    }
    if (!RETRY_STATUSES.has(res.status) || attempt === attempts - 1) return res;
    lastError = res;
  }
  // Unreachable: the loop always returns or throws on its final attempt.
  throw lastError instanceof Error ? lastError : new Error("Request failed");
}

async function responseErrorMessage(res: Response): Promise<string> {
  const body = await res.json().catch(() => null) as { error?: unknown } | null;
  return typeof body?.error === "string" ? body.error : `Request failed (${res.status})`;
}

/**
 * Read a server error message from a non-OK Response, with a caller-supplied
 * fallback. Centralizes the `await res.json().catch(() => ({ error: "…" }))`
 * pattern that was duplicated across the "raw Response" callers (useHub, useChat,
 * useDevhubAssist, PageEditor). Never throws — safe to call on any Response.
 */
export async function readError(res: Response, fallback = "Request failed"): Promise<string> {
  const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
  if (typeof body?.error === "string" && body.error) return body.error;
  return `${fallback} (${res.status})`;
}

/**
 * Parse a Response body as JSON of type T, optionally validating with a type
 * guard. When a guard is supplied and the shape doesn't match, throws rather than
 * handing callers malformed data. Without a guard it's an unchecked `as T` cast —
 * the same trust level as a bare `res.json()`, just centralized.
 */
export async function parseJson<T>(res: Response, guard?: (v: unknown) => v is T): Promise<T> {
  const data: unknown = await res.json();
  if (guard && !guard(data)) throw new Error("Unexpected response shape");
  return data as T;
}

// ── Type guards for the highest-value response shapes ──────────────────────────
// Not exhaustive validation — just enough to catch a wrong-endpoint / garbled
// body early on the few shapes the UI most depends on.
function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}
export function isSession(v: unknown): v is Session {
  return isRecord(v) && typeof v.id === "string" && typeof v.name === "string" && Array.isArray(v.agentIds);
}
export function isAgent(v: unknown): v is Agent {
  return isRecord(v) && typeof v.id === "string" && typeof v.name === "string" && typeof v.status === "string";
}
export function isTask(v: unknown): v is Task {
  return isRecord(v) && typeof v.id === "string" && typeof v.title === "string" && Array.isArray(v.assignedAgentIds);
}
const isArrayOf = <T>(guard: (v: unknown) => v is T) => (v: unknown): v is T[] => Array.isArray(v) && v.every(guard);
export const isAgentArray = isArrayOf(isAgent);
export const isTaskArray = isArrayOf(isTask);

async function apiJson<T>(input: string, init?: RequestInit, guard?: (v: unknown) => v is T): Promise<T> {
  const res = await apiFetch(input, init);
  if (!res.ok) throw new Error(await responseErrorMessage(res));
  return parseJson<T>(res, guard);
}

export const api = {
  // SSE: EventSource can't set an Authorization header, so an authenticated
  // POST mints a short-lived SINGLE-USE ticket that rides the URL instead of
  // the raw bearer token — a logged/leaked URL is worthless within seconds and
  // dies on first use. Minted fresh for every (re)connect, since a reconnect
  // can never redeem the previous ticket again.
  createEventsUrl: async (): Promise<string> => {
    const { ticket } = await apiJson<{ ticket: string }>(`${API_BASE}/api/events/ticket`, { method: "POST" });
    return `${API_BASE}/api/events?ticket=${encodeURIComponent(ticket)}`;
  },

  // The signed-in user, resolved server-side (Okta-verified, or local default).
  //
  // `org` picks which org the DevHub CMS `role`/`team` on the answer are resolved
  // FOR. A role is per org, so the reply has no single correct value without one —
  // unscoped it is Incode's, which is why a caller that gates on the role of the
  // org it is actually looking at has to say so. Optional because the identity half
  // (name, email, admin bit, tenant scope) is org-independent and most callers want
  // only that.
  getMe: (org?: string): Promise<Me> =>
    apiJson(`${API_BASE}/api/me${org ? `?org=${encodeURIComponent(org)}` : ""}`),

  // Orgs to showcase in the left menu — those with knowledge-base content on disk
  // (empty until the Incode-owned KB is synced with INCODE_GH_TOKEN).
  getOrgs: (): Promise<{ orgs: string[] }> => apiJson(`${API_BASE}/api/orgs`),

  // Version badge popover content — recent merged PRs on develop, server-cached.
  getChangelog: (): Promise<Changelog> => apiJson(`${API_BASE}/api/changelog`),

  // ── Admin (Omni Hub app administration) — admin-only, backs the Settings page.
  getAdminStatus: (): Promise<AdminStatus> => apiJson(`${API_BASE}/api/admin/status`),
  // Background-worker heartbeats + process vitals. Backs Settings → System health.
  getSystemHealth: (): Promise<SystemHealth> => apiJson(`${API_BASE}/api/admin/health`),
  // The last N internal log lines from the in-memory ring buffer. `level` keeps
  // that severity and above. Backs Settings → System → Recent logs.
  getInternalLogs: (opts: { level?: string; limit?: number } = {}): Promise<{ entries: InternalLogEntry[] }> => {
    const q = new URLSearchParams();
    if (opts.level) q.set("level", opts.level);
    if (opts.limit) q.set("limit", String(opts.limit));
    const qs = q.toString();
    return apiJson(`${API_BASE}/api/admin/logs${qs ? `?${qs}` : ""}`);
  },
  // Everyone who has signed in, merged with the rights attached to their account
  // (org grants + DevHub role). Backs Settings → Users.
  getOmniUsers: (): Promise<OmniUserEntry[]> => apiJson(`${API_BASE}/api/admin/users`),
  // Attach a user to one or more orgs — an explicit membership grant (org_members)
  // the server unions with the orgs derived from the agents they own
  // (core/authz.ts allowedOrgsFor), so a web-only user gets tenant scope. Passing
  // an empty `orgs` persists an empty grant; use deleteOrgMembers to drop it.
  setOrgMembers: (input: { sub: string; email: string; name?: string | null; orgs: string[] }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/admin/org-members`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  deleteOrgMembers: (sub: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/admin/org-members/delete`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ sub }) }),
  // Deliberately restart THIS container instance — it exits after acknowledging and
  // the container's restart policy brings a fresh one back. Backs the System health
  // "Restart instance" action. Returns 202 { status: "restarting" }.
  restartInstance: (): Promise<Response> =>
    apiFetch(`${API_BASE}/api/admin/restart`, { method: "POST" }),

  // ── The internal Claude process + external MCP connections (admin-only) ──
  // Settings → AI: which model the internal Claude process runs on.
  getClaudeSettings: (): Promise<ClaudeSettings> => apiJson(`${API_BASE}/api/admin/ai`),
  setClaudeModel: (model: string): Promise<ClaudeSettings> =>
    apiJson(`${API_BASE}/api/admin/ai`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ model }) }),
  // One org's connections. The token is write-only: served back only as `hasToken`.
  getGuides: (org: string): Promise<GuideList> =>
    apiJson(`${API_BASE}/api/guides?org=${encodeURIComponent(org)}`),
  getGuidePage: (org: string, collection: string, slug: string): Promise<GuidePageContent> =>
    apiJson(`${API_BASE}/api/guides/${encodeURIComponent(collection)}/${encodeURIComponent(slug)}?org=${encodeURIComponent(org)}`),
  // `hash` is GuidePageContent.hash of the text the edit started from; a stale one is a 409.
  saveGuidePage: (org: string, collection: string, slug: string, input: { content: string; hash: string }): Promise<GuideSaveResult> =>
    apiJson(`${API_BASE}/api/guides/${encodeURIComponent(collection)}/${encodeURIComponent(slug)}?org=${encodeURIComponent(org)}`, { method: "PUT", headers: jsonHeaders, body: JSON.stringify(input) }),
  createGuidePage: (org: string, collection: string, input: GuideCreateInput): Promise<GuideCreateResult> =>
    apiJson(`${API_BASE}/api/guides/${encodeURIComponent(collection)}?org=${encodeURIComponent(org)}`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  // A guide page's image/video, fetched with the bearer token (an <img> can't send
  // one), for the page to show from a blob: URL.
  getGuideAsset: async (org: string, collection: string, slug: string, file: string): Promise<Blob> => {
    const res = await apiFetch(`${API_BASE}/api/guides/${encodeURIComponent(collection)}/${encodeURIComponent(slug)}/assets/${encodeURIComponent(file)}?org=${encodeURIComponent(org)}`);
    if (!res.ok) throw new Error(await responseErrorMessage(res));
    return res.blob();
  },
  getMcpConnections: (org: string): Promise<{ connections: McpConnection[] }> =>
    apiJson(`${API_BASE}/api/mcp/connections?org=${encodeURIComponent(org)}`),
  createMcpConnection: (input: { org: string; name: string; url: string; token?: string }): Promise<McpConnection> =>
    apiJson(`${API_BASE}/api/mcp/connections`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  // `token` replaces it, `clearToken` removes it; omit both to leave it untouched.
  updateMcpConnection: (org: string, name: string, input: { url?: string; token?: string; clearToken?: boolean }): Promise<McpConnection> =>
    apiJson(`${API_BASE}/api/mcp/connections/${encodeURIComponent(org)}/${encodeURIComponent(name)}`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  deleteMcpConnection: (org: string, name: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/mcp/connections/${encodeURIComponent(org)}/${encodeURIComponent(name)}`, { method: "DELETE" }),
  // Connect and read what the server provides; the outcome (overview or error) is on the answer.
  refreshMcpConnection: (org: string, name: string): Promise<McpConnection> =>
    apiJson(`${API_BASE}/api/mcp/connections/${encodeURIComponent(org)}/${encodeURIComponent(name)}/refresh`, { method: "POST" }),
  askMcp: (org: string, name: string, question: string): Promise<McpAskResult> =>
    apiJson(`${API_BASE}/api/mcp/connections/${encodeURIComponent(org)}/${encodeURIComponent(name)}/ask`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ question }) }),

  // ── Presence (humans connected to the hub) ───────────────────────────────
  getPresence: (): Promise<HumanPresence[]> => apiJson(`${API_BASE}/api/presence`),
  // Heartbeat; records the caller's presence. Returns false on network failure.
  presencePing: async (): Promise<boolean> => {
    const res = await apiFetch(`${API_BASE}/api/presence/ping`, { method: "POST" }).catch(() => null);
    return !!res && res.ok;
  },

  // ── Agent Hub ──────────────────────────────────────────────────────────
  getAgents: (): Promise<Agent[]> => apiJson(`${API_BASE}/api/agents`, undefined, isAgentArray),
  getSessions: (): Promise<Session[]> => apiJson(`${API_BASE}/api/sessions`),
  getSession: (id: string): Promise<Session> =>
    apiJson(`${API_BASE}/api/sessions/${id}`),
  postNote: (id: string, content: string) =>
    apiFetch(`${API_BASE}/api/sessions/${id}/note`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ content }),
    }),
  postContext: (id: string, content: string) =>
    apiFetch(`${API_BASE}/api/sessions/${id}/context`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ content }),
    }),
  postApprove: (id: string, approved: boolean) =>
    apiFetch(`${API_BASE}/api/sessions/${id}/approve`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ approved }),
    }),
  // Returns the raw Response so the caller can read the error body on !ok.
  createSession: (name?: string, agentIds: string[] = []): Promise<Response> =>
    apiFetch(`${API_BASE}/api/sessions`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ name, agentIds }),
    }),
  addSessionAgent: (id: string, agentId: string) =>
    apiFetch(`${API_BASE}/api/sessions/${id}/agents`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ agentId }),
    }),
  removeSessionAgent: (id: string, agentId: string) =>
    apiFetch(`${API_BASE}/api/sessions/${id}/agents/remove`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ agentId }),
    }),
  renameSession: (id: string, name: string) =>
    apiFetch(`${API_BASE}/api/sessions/${id}/name`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ name }),
    }),
  deleteSession: (id: string) =>
    apiFetch(`${API_BASE}/api/sessions/${id}/delete`, { method: "POST" }),
  // Toggle link sharing (any signed-in user with the URL may view + post notes).
  shareSession: (id: string, shared: boolean) =>
    apiFetch(`${API_BASE}/api/sessions/${id}/share`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ shared }),
    }),
  // Directory of people to tag into a session (everyone who has signed in).
  getSessionUsers: (): Promise<SessionUser[]> => apiJson(`${API_BASE}/api/sessions/users`),
  addSessionTag: (id: string, user: SessionUser) =>
    apiFetch(`${API_BASE}/api/sessions/${id}/tags`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify(user),
    }),
  removeSessionTag: (id: string, sub: string) =>
    apiFetch(`${API_BASE}/api/sessions/${id}/tags/remove`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ sub }),
    }),

  // ── Tasks ────────────────────────────────────────────────────────────────
  getTasks: (): Promise<Task[]> => apiJson(`${API_BASE}/api/tasks`, undefined, isTaskArray),
  getTask: (id: string): Promise<Task> => apiJson(`${API_BASE}/api/tasks/${id}`),
  // Returns the raw Response so the caller can read the error body on !ok.
  createTask: (input: { title: string; description: string; org: string | null; assignedAgentIds: string[] }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/tasks`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify(input),
    }),
  assignTask: (id: string, agentIds: string[]) =>
    apiFetch(`${API_BASE}/api/tasks/${id}/assign`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ agentIds }),
    }),
  setTaskStatus: (id: string, status: TaskStatus) =>
    apiFetch(`${API_BASE}/api/tasks/${id}/status`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ status }),
    }),
  addTaskInstruction: (id: string, content: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/tasks/${id}/instruction`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ content }),
    }),
  deleteTask: (id: string) =>
    apiFetch(`${API_BASE}/api/tasks/${id}/delete`, { method: "POST" }),

  // ── Repositories ────────────────────────────────────────────────────────
  getRepos: (org: string): Promise<{ repos: RepoEntry[] }> =>
    apiJson(`${API_BASE}/api/repos?org=${encodeURIComponent(org)}`),

  // On-demand container clones (gh repo clone). getRepoClones reports whether the
  // feature is enabled (and why not) plus what this container currently has on
  // disk; clone/pull return 202 + the pending record (status streams over SSE).
  getRepoClones: (): Promise<{ enabled: boolean; reason: string | null; dir: string; total: number; clones: RepoClone[] }> =>
    apiJson(`${API_BASE}/api/repos/clones`),
  cloneRepo: (org: string, name: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/repos/${encodeURIComponent(org)}/${encodeURIComponent(name)}/clone`, { method: "POST" }),
  pullRepo: (org: string, name: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/repos/${encodeURIComponent(org)}/${encodeURIComponent(name)}/pull`, { method: "POST" }),
  removeRepoClone: (org: string, name: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/repos/${encodeURIComponent(org)}/${encodeURIComponent(name)}/clone`, { method: "DELETE" }),
  // Trigger an immediate sync of one org's cloned repos to their latest commit;
  // per-repo changes arrive over SSE. Resolves to the run summary.
  syncRepos: (org?: string): Promise<{ updated: number; unchanged: number; errored: number; skipped: number }> =>
    apiJson(`${API_BASE}/api/repos/sync${org ? `?org=${encodeURIComponent(org)}` : ""}`, { method: "POST" }),
  // Re-clone one org's repos whose clone failed; each repo's progress streams over
  // SSE. Resolves to a summary of how many were (re)started.
  retryFailedClones: (org?: string): Promise<{ started: number; skipped: number }> =>
    apiJson(`${API_BASE}/api/repos/clones/retry-failed${org ? `?org=${encodeURIComponent(org)}` : ""}`, {
      method: "POST",
    }),
  // Clear one org's failed clone records (repos that can't be cloned here); each
  // removal streams over SSE. Resolves to how many rows were cleared.
  clearFailedClones: (org?: string): Promise<{ cleared: number }> =>
    apiJson(`${API_BASE}/api/repos/clones/clear-failed${org ? `?org=${encodeURIComponent(org)}` : ""}`, {
      method: "POST",
    }),

  // Agents Hub → Runtime: autonomous agent runtimes, one per repo. `configured`
  // reports whether the launcher is wired up here at all (false locally, where
  // there is no ECS) — a 200 with a reason, deliberately not an error, since
  // "nothing can be launched here" and "we couldn't look" need different fixes.
  // `entries` is the full ELIGIBLE set (every repo exposing .agents/), not only
  // what has been started: an unstarted repo comes back stopped with a null runtime.
  getAgentRuntimes: (): Promise<{
    configured: boolean;
    reason: string | null;
    total: number;
    counts: Record<AgentRuntimeState, number>;
    entries: AgentRuntimeEntry[];
  }> => apiJson(`${API_BASE}/api/runtime`),
  // Start returns 202: the container exists but has not registered yet, so the row
  // is `starting` and settles asynchronously. Start/reauth carry the plaintext
  // credential in their ONLY response — it is never retrievable again.
  startAgentRuntime: (org: string, repo: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/runtime/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/start`, { method: "POST" }),
  stopAgentRuntime: (org: string, repo: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/runtime/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/stop`, { method: "POST" }),
  // Reads the LAST task's stream, so the logs of a run that just failed are still
  // reachable after it stopped. `configured:false` when no log group is wired up.
  getAgentRuntimeLogs: (
    org: string,
    repo: string,
  ): Promise<
    | { configured: true; stream: string; lines: { time: string; message: string }[] }
    | { configured: false; reason: string }
  > => apiJson(`${API_BASE}/api/runtime/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/logs`),
  // Per-repo settings. `effort` is a launch override, so the response reports
  // whether the change is live or pending a restart.
  updateAgentRuntime: (
    org: string,
    repo: string,
    patch: { writeEnabled?: boolean; effort?: AgentEffort },
  ): Promise<{ runtime: AgentRuntime; appliesOnRestart?: boolean }> =>
    apiJson(`${API_BASE}/api/runtime/${encodeURIComponent(org)}/${encodeURIComponent(repo)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }),
  reauthAgentRuntime: (org: string, repo: string): Promise<{ token: string; runtime: AgentRuntime; note: string }> =>
    apiJson(`${API_BASE}/api/runtime/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/reauth`, { method: "POST" }),

  // AI-native readiness re-scan. getRepoReadiness reports whether scanning is
  // available (a GitHub token) plus the last run's status and stored results;
  // scanRepoReadiness starts an org-wide background scan (202 + initial status),
  // with per-repo results + progress streaming over SSE.
  getRepoReadiness: (): Promise<{
    enabled: boolean;
    reason: string | null;
    status: RepoReadinessScanStatus;
    total: number;
    readiness: RepoReadiness[];
  }> => apiJson(`${API_BASE}/api/repos/readiness`),
  scanRepoReadiness: (org?: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/repos/readiness/scan${org ? `?org=${encodeURIComponent(org)}` : ""}`, {
      method: "POST",
    }),

  // ── Knowledge base (runtime-cloned content repo) ───────────────────────────
  // Status of the omni-system-knowledge-base clone that provides all served
  // content. cloneKnowledgeBase triggers a clone/refresh (admin); progress streams
  // over SSE (knowledge_base_update).
  getKnowledgeBase: (): Promise<KnowledgeBaseStatus> => apiJson(`${API_BASE}/api/knowledge-base`),
  cloneKnowledgeBase: (): Promise<Response> =>
    apiFetch(`${API_BASE}/api/knowledge-base/clone`, { method: "POST" }),

  // ── System Docs ──────────────────────────────────────────────────────────
  getDocs: (): Promise<DocEntry[]> => apiJson(`${API_BASE}/api/docs`),
  getDocText: (path: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/docs/${path.split("/").map(encodeURIComponent).join("/")}`),

  // ── Knowledge Graph ────────────────────────────────────────────────────────
  // system/org/<org>/knowledge-graph/: one entry per thing that exists, holding
  // its relations and pointing at the system doc that describes it. Admin-only —
  // the API 403s non-admins.
  getGraphSummary: (org: string): Promise<GraphSummary> =>
    apiJson(`${API_BASE}/api/knowledge-graph/${encodeURIComponent(org)}`),
  getGraphEntries: (org: string, type?: string): Promise<GraphEntry[]> =>
    apiJson(`${API_BASE}/api/knowledge-graph/${encodeURIComponent(org)}/entries${
      type ? `?type=${encodeURIComponent(type)}` : ""}`),
  searchGraph: (org: string, q: string): Promise<GraphEntry[]> =>
    apiJson(`${API_BASE}/api/knowledge-graph/${encodeURIComponent(org)}/search?q=${encodeURIComponent(q)}`),
  // `ref` is an id ("service/omni-api"), a slug, or any alias ("user-service").
  // The response carries both rails, already grouped by relation.
  getGraphEntry: (org: string, ref: string): Promise<GraphEntryDetail> =>
    apiJson(`${API_BASE}/api/knowledge-graph/${encodeURIComponent(org)}/entry/${
      ref.split("/").map(encodeURIComponent).join("/")}`),
  // An entry's `page` — org-relative ("docs/services/omni-api.md"). It is a
  // system doc; this route reads it behind the graph's admin gate, which is how
  // the docs/people/ and docs/teams/ pages stay off the open /api/docs route.
  getGraphPageText: (org: string, orgPath: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/knowledge-graph/${encodeURIComponent(org)}/page/${
      orgPath.split("/").map(encodeURIComponent).join("/")}`),

  // ── DevHub Docs (public, customer-facing developer documentation) ───────────
  getDevhubDocs: (version?: string): Promise<DocEntry[]> =>
    apiJson(`${API_BASE}/api/devhub${version ? `?version=${encodeURIComponent(version)}` : ""}`),
  getDevhubDocText: (path: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/${path.split("/").map(encodeURIComponent).join("/")}`),

  // DevHub structure: canonical section order + orgs that have a corpus. The Web UI
  // reads ordering/availability from here instead of hardcoding them.
  getDevhubStructure: (): Promise<{ sections: string[]; sectionFileOrder: string[]; sectionFileOrderByOrg: Record<string, Record<string, string[]>>; orgs: string[]; versions: string[]; versionsByOrg: Record<string, string[]>; pageCounts: Record<string, Record<string, { docs: number; apiRef: number }>> }> =>
    apiJson(`${API_BASE}/api/devhub/structure`),

  // Folder-per-page model: list pages, and read one page's current+suggested versions.
  // `org` is worth passing wherever the caller renders one org: this is the
  // largest body the API sends and it cannot be cached, so an unscoped fetch
  // downloads every org's pages to discard all but one (ENG-54607).
  getDevhubPages: (version?: string, org?: string): Promise<DevhubPageEntry[]> => {
    const q = new URLSearchParams();
    if (version) q.set("version", version);
    if (org) q.set("org", org);
    const query = q.toString();
    return apiJson(`${API_BASE}/api/devhub/pages${query ? `?${query}` : ""}`);
  },
  getDevhubPage: (path: string): Promise<DevhubPageContent> =>
    apiJson(`${API_BASE}/api/devhub/page/${path.split("/").map(encodeURIComponent).join("/")}`),

  // Prebuilt search index over every page, for the public site's client-side search.
  getDevhubSearchIndex: (): Promise<DevhubSearchEntry[]> =>
    apiJson(`${API_BASE}/api/devhub/search-index`),

  // Raw home.md (the version landing doc) — the structured hero + browse-by-topic
  // source the public home renders from. 404 when a version has no home.md; the
  // caller treats a non-OK response as "use generic defaults".
  getDevhubHomeDoc: (org: string, version: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/home-doc?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),

  // Publish the generic-home (home.md) for an org+version by opening a PR against
  // the knowledge-base repo. `content` is the composed home.md (frontmatter + the
  // structured sections). Raw Response so the caller can surface publish errors.
  publishDevhubHome: (org: string, version: string, content: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/home/publish`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, version, content }),
    }),

  // ── DevHub Docs CMS — authoring (drafts, workflow, RBAC, audit) ─────────────
  // ONE org's drafts — the server requires it (an unscoped list put every org's
  // pages in the Review queue).
  getDrafts: (org: string): Promise<DevhubDraft[]> =>
    apiJson(`${API_BASE}/api/devhub/drafts?org=${encodeURIComponent(org)}`),
  getOpenDraft: (pagePath: string): Promise<DevhubDraft | null> =>
    apiJson(`${API_BASE}/api/devhub/drafts/open?pagePath=${encodeURIComponent(pagePath)}`),
  // The page's current CMS state — the latest draft INCLUDING a published one, so
  // the editor shows "Published" for a published page instead of a fresh draft.
  getLatestDraft: (pagePath: string): Promise<DevhubDraft | null> =>
    apiJson(`${API_BASE}/api/devhub/drafts/latest?pagePath=${encodeURIComponent(pagePath)}`),
  getDraft: (id: string): Promise<DevhubDraft> =>
    apiJson(`${API_BASE}/api/devhub/drafts/${encodeURIComponent(id)}`),
  // Create (or reuse) the open draft for a page. Raw Response so callers can read
  // errors. The draft's team is server-derived from the page's section.
  createDraft: (pagePath: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/drafts`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ pagePath }),
    }),
  // Create a draft for a NET-NEW page (no existing page.yml). The server validates
  // the slug (URL-safe + unique within the version) and returns the seeded draft;
  // its `pagePath` opens the editor. Raw Response so callers can read errors.
  // `landing` marks the page as its container's landing page (page.yml `landing: true`).
  // Required when the target creates a section or folder — the server refuses otherwise.
  /** The new-page create's own slug rule, asked as the writer types (ENG-57944).
   *  `hold` is null when the slug is free. */
  checkNewPageSlug: (q: { org: string; version: string; section: string; slug: string }, signal?: AbortSignal): Promise<{ hold: unknown }> =>
    apiFetch(`${API_BASE}/api/devhub/new-page/slug-check?${new URLSearchParams(q).toString()}`, { signal }).then((r) => {
      if (!r.ok) throw new Error(`slug check failed (${r.status})`);
      return r.json() as Promise<{ hold: unknown }>;
    }),
  createNewPageDraft: (input: { org: string; version: string; section: string; group?: string | null; title: string; slug: string; landing?: boolean; parentPagePath?: string }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/drafts/new`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify(input),
    }),
  saveDraft: (id: string, fields: { title?: string; body?: string }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/drafts/${encodeURIComponent(id)}`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify(fields),
    }),
  // `comment` is the reviewer's note (request_changes); `reason` is the tech
  // writer's justification for skipping SME review. Two fields, not one: the
  // Audit log's REASON column shows only the latter, so sharing a field would
  // spill every review note into it.
  /** `secondReviewer` is the teammate an SME's submit is routed to, defaulting to
   *  their team's lead (ENG-54991). Omitted for every other action and by every
   *  other role, where the server keeps the original section-wide routing. */
  transitionDraft: (id: string, action: string, comment?: string, reason?: string, secondReviewer?: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/drafts/${encodeURIComponent(id)}/transition`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ action, comment, reason, secondReviewer }),
    }),
  // The Release Notes feed (ENG-62099): a page's Product name (what makes it a
  // release notes page), and a draft's answer to the feed card — stored on the
  // draft so a batch publish carries it too.
  getReleaseFeedPage: (pagePath: string): Promise<{ eligible: boolean; product: string | null; suggestions: string[] }> =>
    apiJson(`${API_BASE}/api/devhub/release-feed/page?pagePath=${encodeURIComponent(pagePath)}`),
  setReleaseFeedPage: (pagePath: string, product: string | null): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/release-feed/page`, {
      method: "PUT", headers: jsonHeaders, body: JSON.stringify({ pagePath, product }),
    }),
  // Page properties (ENG-62136): the "Properties for filterable pages" registry,
  // each page's values, and the working copy the preview's Filter pills read.
  // Writes answer a cross-page staging (a prompt writes nothing; the answer comes
  // back as batchId / createNew), so they return the Response.
  getDevhubProperties: (org: string): Promise<{ properties: DevhubPropertyWithUsage[] }> =>
    apiJson(`${API_BASE}/api/devhub/properties?org=${encodeURIComponent(org)}`),
  getDevhubPropertyValues: (org: string, version: string): Promise<DevhubPagePropertiesFile> =>
    apiJson(`${API_BASE}/api/devhub/properties/values?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  getDevhubPageProperties: (pagePath: string): Promise<DevhubPagePropertiesRead> =>
    apiJson(`${API_BASE}/api/devhub/properties/page?pagePath=${encodeURIComponent(pagePath)}`),
  setDevhubPageProperty: (pagePath: string, key: string, values: string[], answer: { batchId?: string; createNew?: boolean } = {}): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/properties/page`, {
      method: "PUT", headers: jsonHeaders, body: JSON.stringify({ pagePath, key, values, ...answer }),
    }),
  createDevhubProperty: (org: string, input: { key: string; label: string; description: string; values: string[] }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/properties`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, ...input }) }),
  editDevhubProperty: (org: string, key: string, input: { label?: string; description?: string }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/properties/${encodeURIComponent(key)}`, { method: "PATCH", headers: jsonHeaders, body: JSON.stringify({ org, ...input }) }),
  deleteDevhubProperty: (org: string, key: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/properties/${encodeURIComponent(key)}?org=${encodeURIComponent(org)}`, { method: "DELETE" }),
  addDevhubPropertyValue: (org: string, key: string, label: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/properties/${encodeURIComponent(key)}/values`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, label }) }),
  renameDevhubPropertyValue: (org: string, key: string, value: string, label: string, answer: { batchId?: string; createNew?: boolean } = {}): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/properties/${encodeURIComponent(key)}/values/${encodeURIComponent(value)}`, {
      method: "PATCH", headers: jsonHeaders, body: JSON.stringify({ org, label, ...answer }),
    }),
  removeDevhubPropertyValue: (org: string, key: string, value: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/properties/${encodeURIComponent(key)}/values/${encodeURIComponent(value)}?org=${encodeURIComponent(org)}`, { method: "DELETE" }),
  restoreDevhubPropertyValue: (org: string, key: string, value: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/properties/${encodeURIComponent(key)}/values/${encodeURIComponent(value)}/restore`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org }),
    }),
  getDevhubPropertyRetiring: (org: string): Promise<{ rows: DevhubPropertyRetiringRow[] }> =>
    apiJson(`${API_BASE}/api/devhub/properties/retiring?org=${encodeURIComponent(org)}`),
  getDevhubPropertyPending: (org: string, version: string): Promise<{ pages: string[]; batch: { id: string; name: string } | null }> =>
    apiJson(`${API_BASE}/api/devhub/properties/pending?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  discardDevhubPropertyChanges: (org: string, version: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/properties/discard`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, version }) }),
  setDraftReleaseFeed: (id: string, choice: DevhubReleaseFeedChoice): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/drafts/${encodeURIComponent(id)}/release-feed`, {
      method: "PUT", headers: jsonHeaders, body: JSON.stringify({ choice }),
    }),
  publishDraft: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/drafts/${encodeURIComponent(id)}/publish`, { method: "POST" }),
  deleteDraft: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/drafts/${encodeURIComponent(id)}/delete`, { method: "POST" }),

  // Set a page's PUBLIC visibility (Live/Hidden). MERGE-GATED: the server opens the
  // corpus page.yml PR and squash-merges it, and only a merged PR changes anything
  // — 2xx { ok, applied: true, visibility, prUrl } means the deployed site will
  // match on its next build. Anything else changed NOTHING (501 no publish token,
  // 409 { prUrl } the PR wouldn't auto-merge, 502 GitHub). Raw Response so callers
  // can read that body and surface the PR to merge by hand.
  // Rename a page's slug. One corpus PR carries the folder move AND every link
  // rewrite; the redirect row is written only once that PR merges.
  renameDevhubPageSlug: (pagePath: string, slug: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/pages/slug`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ pagePath, slug }),
    }),
  // Delete a page (ENG-55775). One corpus PR removes the page's folder AND ships
  // the redirect manifest; the redirect row and the 30-day recovery record are
  // written only once that PR merges.
  deleteDevhubPage: (input: {
    pagePath: string;
    /** the page readers land on instead — required unless the page was only
     *  ever published Hidden, which the server decides (ENG-60548) */
    redirectTo: string;
    /** the page's title, typed out; checked server-side too */
    confirmTitle: string;
  }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/pages/delete`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify(input),
    }),
  /** Whether deleting this page needs a redirect (ENG-60548). Advisory: the
   *  delete route re-derives it, so this only decides what the dialog shows. */
  getDeleteRequirement: (pagePath: string): Promise<{ redirectRequired: boolean; reason: string }> =>
    apiJson(`${API_BASE}/api/devhub/pages/delete/requirement?pagePath=${encodeURIComponent(pagePath)}`),
  // Post-delete link reports with rows still needing a decision (ENG-55775).
  getDeletedLinkReports: (org: string, version: string): Promise<DeletedLinkReportData[]> =>
    apiJson(`${API_BASE}/api/devhub/pages/deleted/reports?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  /** Dismiss (or re-open) one row of a post-delete report. */
  setDeletedLinkEntry: (input: { pagePath: string; key: string; state: "open" | "dismissed"; report?: "delete" | "restore" }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/pages/deleted/entry`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify(input),
    }),
  /** The Recently deleted view: still-recoverable pages, plus restored ones with
   *  post-restore work outstanding (ENG-55775 part 3). */
  getDeletedPages: (org: string, version: string): Promise<DeletedPageRow[]> =>
    apiJson(`${API_BASE}/api/devhub/pages/deleted?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  /** Put a deleted page back — writes its files and removes the redirect. */
  restoreDeletedPage: (pagePath: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/pages/deleted/restore`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ pagePath }),
    }),
  /** End the recovery window early, freeing the slug and dropping the redirect. */
  purgeDeletedPage: (pagePath: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/pages/deleted/purge`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ pagePath }),
    }),
  /** Discarded drafts still inside their 30-day window (ENG-58552). */
  getDiscardedDrafts: (org: string, version: string): Promise<DevhubDiscardedDraftRow[]> =>
    apiJson(`${API_BASE}/api/devhub/discarded-drafts?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  /** Put a discarded draft back, in Draft status. */
  restoreDiscardedDraft: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/discarded-drafts/restore`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ id }),
    }),
  /** End a discarded draft's recovery window early. */
  purgeDiscardedDraft: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/discarded-drafts/purge`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ id }),
    }),
  setDevhubPageVisibility: (path: string, visibility: "live" | "hidden"): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/pages/visibility`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ pagePath: path, visibility }),
    }),

  // The Checks tab's link scan: every link in the corpus resolved the way the
  // deployed site resolves it (redirect layer included), with a suggested fix.
  // Static — no outbound request is made for any link. Reading every page body is
  // expensive, so the server memoizes per corpus revision; `refresh` re-scans.
  getDevhubLinkCheck: (org = "incode", version?: string, refresh = false): Promise<DevhubLinkReport> =>
    apiJson(`${API_BASE}/api/devhub/checks/links?org=${encodeURIComponent(org)}${version ? `&version=${encodeURIComponent(version)}` : ""}${refresh ? "&refresh=1" : ""}`),

  // Every bookmark link that doesn't land on a heading (ENG-57594). Its own
  // scan rather than a filter over the link report: it also reads DRAFT bodies,
  // which move far more often than the corpus revision a cache would key on,
  // so it is deliberately not memoized and takes no `refresh`.
  getDevhubBookmarkReport: (org: string, version: string): Promise<DevhubBookmarkReport> =>
    apiJson(`${API_BASE}/api/devhub/checks/bookmarks?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),

  // Take one suggested fix from the Links report: the href is rewritten in the
  // page's draft and the page is filed into a batch, in one request. Nothing is
  // published — the page goes through the normal workflow inside that batch.
  // A 409 carrying `conflict` means the page is already in another active batch,
  // answerable by resending with `move: true`.
  acceptDevhubLinkFix: (input: {
    pagePath: string;
    href: string;
    suggestion: string;
    line?: number | null;
    batchId?: string;
    newBatch?: { name: string; description?: string | null; plannedPublishAt?: string | null };
    move?: boolean;
    /** Take the page straight to TW final · Approved in the same request
     *  (ENG-55762). Omitted means the normal workflow, as before. */
    approve?: boolean;
    /** The deleted page whose post-delete report this accept came from, so the
     *  matching row is marked resolved in the same request (ENG-55775). */
    deletedPage?: string;
    /** Which of that record's two reports the row belongs to (ENG-55775 part 3). */
    report?: "delete" | "restore";
  }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/checks/links/accept`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify(input),
    }),

  // Hotlinked images — asset references the corpus does not own, so Assets can't
  // see them and every per-section count is short by that many. Static: nothing is
  // fetched from the hosts it names. Memoized per corpus revision like the link
  // check; `refresh` re-scans.
  getDevhubHotlinkCheck: (org = "incode", version?: string, refresh = false): Promise<DevhubHotlinkReport> =>
    apiJson(`${API_BASE}/api/devhub/checks/hotlinks?org=${encodeURIComponent(org)}${version ? `&version=${encodeURIComponent(version)}` : ""}${refresh ? "&refresh=1" : ""}`),

  // Visibility drift — pages the CMS records as Hidden whose page.yml in the
  // knowledge base doesn't, so the deployed site still serves them. Residue of the
  // toggle's old best-effort publish; the toggle is merge-gated now and can't add
  // to it. `configured:false` means "we couldn't look" (no publish token), which
  // is deliberately NOT the same as a clean corpus.
  getDevhubVisibilityDrift: (org = "incode"): Promise<DevhubVisibilityDrift> =>
    apiJson(`${API_BASE}/api/devhub/pages/visibility/drift?org=${encodeURIComponent(org)}`),

  // Fix all of it in ONE PR (server re-plans; nothing about what's written comes
  // from here). Only ever hides — it never makes a page public.
  reconcileDevhubVisibility: (org = "incode"): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/pages/visibility/reconcile`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org }),
    }),

  // The audit trail, in one of two shapes.
  //
  // ONE PAGE's history (shown inline in the page editor) — `page` is a served path
  // and already names its org, so it carries no org of its own: a second parameter
  // could only agree with the path or contradict it.
  //
  // THE ORG's recent trail — `org` is required there (the route answers 400
  // without one). It used to be every org's most recent 200 entries pooled, which
  // on the tab people open precisely to find out who changed something meant
  // reading another org's history and losing the tail of your own.
  getDevhubAudit: (opts: { page: string } | { org: string }): Promise<DevhubAuditEntry[]> =>
    apiJson(`${API_BASE}/api/devhub/audit?${"page" in opts
      ? `page=${encodeURIComponent(opts.page)}`
      : `org=${encodeURIComponent(opts.org)}`}`),

  // ── DevHub page feedback (thumbs up/down + comment) ────────────────────────
  // Submit is open to every signed-in role, including reader; list/reply/resolve
  // are filtered/gated server-side (see cms-feedback.ts's direct session checks).
  getDevhubFeedback: (pagePath: string): Promise<DevhubFeedback[]> =>
    apiJson(`${API_BASE}/api/devhub/feedback?pagePath=${encodeURIComponent(pagePath)}`),
  // Every open feedback row the caller may see, across every page (Settings → Feedback).
  getDevhubOpenFeedback: (org: string): Promise<DevhubFeedback[]> =>
    apiJson(`${API_BASE}/api/devhub/feedback/open?org=${encodeURIComponent(org)}`),
  submitDevhubFeedback: (pagePath: string, rating: "up" | "down", comment?: string | null): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/feedback`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ pagePath, rating, comment: comment ?? null }),
    }),
  replyDevhubFeedback: (id: string, body: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/feedback/${encodeURIComponent(id)}/reply`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ body }),
    }),
  // Closing a submission: DISMISSED (disregarded) or RESOLVED (addressed), always
  // with a reason (ENG-56565). Either way a soft hide, not a delete — the row
  // leaves the lists but its vote keeps counting toward the page's thumbs score.
  // The action is the ROUTE, never a field, so a client cannot record one act as
  // the other.
  closeDevhubFeedback: (
    id: string,
    action: DevhubFeedbackAction,
    reason: string,
  ): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/feedback/${encodeURIComponent(id)}/${action === "resolved" ? "resolve" : "dismiss"}`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ reason }),
    }),
  // The same for a LIVE-SITE submission. Addressed by its content, not an id — the
  // deployed site's rows have none — so the whole submission goes back exactly as
  // it was listed; the server fingerprints it. See devhub-feedback-dismissal.ts.
  closeDevhubLiveFeedback: (
    item: { pagePath: string; rating: "up" | "down"; submittedAt: string; comment: string | null },
    action: DevhubFeedbackAction,
    reason: string,
  ): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/feedback/live/${action === "resolved" ? "resolve" : "dismiss"}`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ ...item, reason }),
    }),

  // A writer's verdict on one submission — spam, or genuine after all
  // (ENG-55517). Both directions are one call: the classifier is conservative
  // and its errors are not symmetric, so restoring has to be as easy as filing.
  markDevhubFeedbackSpam: (id: string, decision: "spam" | "not_spam"): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/feedback/${encodeURIComponent(id)}/spam`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ decision }),
    }),
  // The same for a deployed-site submission, which has no id: it is addressed by
  // its exact content, which the server hashes into one (see the live dismissal).
  markDevhubLiveFeedbackSpam: (
    item: { pagePath: string; rating: "up" | "down"; submittedAt: string; comment: string | null },
    decision: "spam" | "not_spam",
  ): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/feedback/live/spam`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ ...item, decision }),
    }),
  // The DEPLOYED site's own feedback — a different store entirely (the DevHub
  // stack's DynamoDB table, read by devhub-feedback-live.ts), so read-only here:
  // those rows have no id, no replies and no resolve. `configured: false` comes
  // back as a normal 200 when the store isn't wired up yet; a real read failure
  // is a 502 and surfaces as an error.
  // TWO functions, not one taking `(org, pagePath?)`: both arguments are strings,
  // so when the multi-org change prepended `org` the page panel's one-argument
  // call kept compiling — with the page PATH now sent as `?org=` — and every
  // page's panel answered 400 and showed "Couldn't read the deployed site's
  // feedback" while the Feedback tab beside it loaded fine (ENG-59430).
  //
  // The whole-site feed is per org (there is no path to derive it from, and no
  // default — an omitted org would show one org's reader feedback on another's tab).
  getDevhubLiveFeedback: (org: string): Promise<DevhubLiveFeedbackFeed> =>
    apiJson(`${API_BASE}/api/devhub/feedback/live?org=${encodeURIComponent(org)}`),
  // ONE page's live feedback (two fixed DynamoDB Queries) — what a page's own
  // feedback panel uses. The server takes the org from the path itself, so the
  // two can never disagree about which site's store a page's feedback comes from.
  getDevhubLivePageFeedback: (pagePath: string): Promise<DevhubLiveFeedbackFeed> =>
    apiJson(`${API_BASE}/api/devhub/feedback/live?pagePath=${encodeURIComponent(pagePath)}`),

  // ── DevHub reusable components (version-scoped snippets embedded in pages) ───
  // List = the union of published (corpus) + working-copy (DB) components for one
  // org+version, each flagged published/dirty. The registry (slug → body) is what
  // the prose transform expands `<Component slug/>` embeds against (working copy
  // wins so preview reflects current edits).
  getDevhubComponents: (org: string, version: string): Promise<DevhubComponentSummary[]> =>
    apiJson(`${API_BASE}/api/devhub/components?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  getDevhubComponentRegistry: (org: string, version: string): Promise<DevhubComponentRegistry> =>
    apiJson(`${API_BASE}/api/devhub/components/registry?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  getDevhubComponent: (org: string, version: string, slug: string): Promise<DevhubComponent> =>
    apiJson(`${API_BASE}/api/devhub/components/one?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}&slug=${encodeURIComponent(slug)}`),
  createDevhubComponent: (input: { org: string; version: string; slug: string; title: string; description?: string | null }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/components`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  // `currentPage` is the page the component is being edited over, if any: that
  // page carries the edit in its own workflow and is not forked (ENG-62129).
  saveDevhubComponent: (id: string, fields: { title?: string; description?: string | null; body?: string }, currentPage?: string | null): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/components/save`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ id, ...fields, ...(currentPage ? { currentPage } : {}) }) }),
  // Answer the "which batch?" question for a component edit or image replace
  // (ENG-62129). The server re-derives the affected pages itself.
  stageDevhubChange: (input: { kind: "component" | "image"; ref: string; batchId?: string; createNew?: boolean; currentPage?: string | null }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/batches/stage-change`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  deleteDevhubComponent: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/components/delete`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ id }) }),
  /** Which pages embed each component — read when the editor opens, not with the
   *  list: it walks the corpus. */
  getDevhubComponentUsage: (org: string, version: string): Promise<DevhubComponentUsage> =>
    apiJson(`${API_BASE}/api/devhub/components/usage?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  submitDevhubComponent: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/components/submit`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ id }) }),
  unsubmitDevhubComponent: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/components/unsubmit`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ id }) }),

  // ── DevHub inline variables (one value, referenced from pages as {{$slug}}) ───
  // Version-scoped like components. The registry is what the preview substitutes
  // against (working values overlaid on published ones); the deployed site reads
  // the published corpus file instead, which is why a page publish is validated
  // against that and not against this.
  getDevhubVariables: (org: string, version: string): Promise<DevhubVariableSummary[]> =>
    apiJson(`${API_BASE}/api/devhub/variables?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  getDevhubVariableRegistry: (org: string, version: string): Promise<Record<string, string>> =>
    apiJson(`${API_BASE}/api/devhub/variables/registry?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  /** Which pages reference each variable (drafts included), and the slugs nothing
   *  defines. A corpus walk — called on tab load, not per render. */
  getDevhubVariableUsage: (org: string, version: string): Promise<DevhubVariableUsage> =>
    apiJson(`${API_BASE}/api/devhub/variables/usage?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  createDevhubVariable: (input: {
    org: string; version: string; slug: string; value: string; description?: string | null;
  }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/variables`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  /** `categoryIds` files the variable (ENG-55728) — assignment, not content, so
   *  it never marks the value unpublished. */
  saveDevhubVariable: (id: string, fields: { value?: string; description?: string | null; categoryIds?: string[] }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/variables/save`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ id, ...fields }) }),
  publishDevhubVariable: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/variables/publish`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ id }) }),
  /** `confirm` answers the 409 a delete gets while pages still reference the
   *  variable — the server re-checks either way. */
  deleteDevhubVariable: (id: string, confirm?: boolean): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/variables/delete`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ id, ...(confirm ? { confirm: true } : {}) }),
    }),

  // ── DevHub AI context (what the deployed site publishes for machines) ───────
  // Three reads, deliberately separate: the inventory costs a round of network
  // probes against the live site plus a corpus walk, while the spend report costs
  // a DynamoDB read and changes whenever the day range does. One call over both
  // would re-probe the site every time somebody pressed "30 days".
  getDevhubAiContext: (org: string, version: string): Promise<DevhubAiContextReport> =>
    apiJson(`${API_BASE}/api/devhub/ai/context?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  // `org` selects which deployed site's spend counters to read, and the server has
  // no default for it: attributing one org's bill to another is not a mild wrong
  // answer, so an omitted org is a 400 rather than Incode's numbers.
  getDevhubAiUsage: (org: string, days: number): Promise<DevhubAiUsageReport> =>
    apiJson(`${API_BASE}/api/devhub/ai/usage?org=${encodeURIComponent(org)}&days=${days}`),
  getDevhubAiGuidance: (org: string, version: string): Promise<DevhubAiGuidanceState> =>
    apiJson(`${API_BASE}/api/devhub/ai/guidance?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  /** Save the working copy. Validated server-side too — the cap and the no-HTML
   *  rule are the publish's rules, and a save that accepted what the publish will
   *  refuse is how a writer meets a rule by being blocked by it. */
  saveDevhubAiGuidance: (org: string, version: string, text: string): Promise<Response> =>
    apiFetch(
      `${API_BASE}/api/devhub/ai/guidance?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`,
      { method: "PUT", headers: jsonHeaders, body: JSON.stringify({ text }) },
    ),
  publishDevhubAiGuidance: (org: string, version: string): Promise<Response> =>
    apiFetch(
      `${API_BASE}/api/devhub/ai/guidance/publish?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`,
      { method: "POST" },
    ),

  /** The authored Ask-AI settings — the editable half of the system prompt plus
   *  the retrieval knobs. Read at the view tier; WRITTEN at the admin tier, which
   *  the response's own `canPublish` reports so the pane can render read-only for
   *  everybody else rather than offering a button that 403s. */
  getDevhubAiSettings: (org: string, version: string): Promise<DevhubAiSettingsState> =>
    apiJson(`${API_BASE}/api/devhub/ai/settings?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  /** `reset: true` returns the prompt body to the shipped default — a write of
   *  the default, not a delete, so the publish record survives it. */
  saveDevhubAiSettings: (
    org: string,
    version: string,
    fields: { systemBody?: string; retrievePages?: number; maxPageChars?: number; reset?: boolean },
  ): Promise<Response> =>
    apiFetch(
      `${API_BASE}/api/devhub/ai/settings?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`,
      { method: "PUT", headers: jsonHeaders, body: JSON.stringify(fields) },
    ),
  publishDevhubAiSettings: (org: string, version: string): Promise<Response> =>
    apiFetch(
      `${API_BASE}/api/devhub/ai/settings/publish?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`,
      { method: "POST" },
    ),

  // ── DevHub variable categories (CMS-only labels for the Variables tab) ──────
  getDevhubVariableCategories: (org: string, version: string): Promise<DevhubVariableCategory[]> =>
    apiJson(`${API_BASE}/api/devhub/variable-categories?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  createDevhubVariableCategory: (input: { org: string; version: string; slug: string; label: string }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/variable-categories`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  renameDevhubVariableCategory: (id: string, label: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/variable-categories/save`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ id, label }) }),
  deleteDevhubVariableCategory: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/variable-categories/delete`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ id }) }),

  // ── DevHub page tags (content ownership that doesn't follow the section tree) ─
  // Org-scoped, and tech-writer/admin only — reads included, because the tag list
  // IS the ownership map and names pages across every section (see cms-tags.ts).
  getDevhubTags: (org: string): Promise<DevhubTag[]> =>
    apiJson(`${API_BASE}/api/devhub/tags?org=${encodeURIComponent(org)}`),
  // The tags carrying ONE page — what a page's own tag control reads.
  getDevhubTagsForPage: (pagePath: string): Promise<DevhubTag[]> =>
    apiJson(`${API_BASE}/api/devhub/tags/for-page?pagePath=${encodeURIComponent(pagePath)}`),
  createDevhubTag: (input: { org: string; slug: string; label?: string; description?: string | null }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/tags`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  // Label/description only — the slug is immutable (it is half the id, which SME
  // tag assignments reference).
  updateDevhubTag: (id: string, fields: { label?: string; description?: string | null }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/tags/${encodeURIComponent(id)}/update`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(fields) }),
  addDevhubTagPages: (id: string, pagePaths: string[]): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/tags/${encodeURIComponent(id)}/pages`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ pagePaths }) }),
  removeDevhubTagPages: (id: string, pagePaths: string[]): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/tags/${encodeURIComponent(id)}/pages/remove`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ pagePaths }) }),
  // The CALLER'S OWN reachable pages (their assigned tags' pages). Any CMS role
  // may read its own scope — it returns page paths, never the taxonomy.
  // The caller's OWN resolved scope — sections and tag-reachable pages. `teams` is
  // optional so a bundle held against an older API still reads the tag half.
  // Per ORG: the scope is resolved against that org's role and teams.
  getDevhubTagScope: (org: string): Promise<{ unrestricted: boolean; teams?: string[]; pagePaths: string[] }> =>
    apiJson(`${API_BASE}/api/devhub/tags/scope?org=${encodeURIComponent(org)}`),
  // Whether ONE page is TW-owned — a boolean, not the page's tags, so any CMS role
  // may ask. The workflow rail and the editor's submit buttons need it, and the
  // person doing the work may be an SME who can't read the taxonomy.
  getDevhubPageOwnership: (pagePath: string): Promise<{
    pagePath: string; twOwned: boolean; sdkOwned?: boolean; sdkLabel?: string | null;
    /** a rename or a Live staged for the SDK repo and not sent yet (ENG-62185) */
    sdkStaged?: DevhubSdkStagedChanges | null;
  }> =>
    apiJson(`${API_BASE}/api/devhub/tags/ownership?pagePath=${encodeURIComponent(pagePath)}`),
  // Take back a rename or a Live staged for an SDK-owned page (ENG-62185).
  cancelDevhubSdkStaged: (pagePath: string, kind: "rename" | "visibility"): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/pages/sdk-staged/cancel`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ pagePath, kind }),
    }),
  // Submit an SDK-owned page as an off-cycle update instead of publishing it
  // (ENG-57133). Nothing is published: the body is staged against the SDK
  // repo's integration branch, and Sync ▸ SDKs sends it to the SDK team.
  // `intoSlug` confirms sending it to the repo's same-titled page instead (the
  // `twin` a 409 names), rather than adding the page to the repo a second time.
  submitDraftOffCycle: (id: string, note: string, intoSlug?: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/drafts/${encodeURIComponent(id)}/submit-off-cycle`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify(intoSlug ? { note, intoSlug } : { note }),
    }),
  // Seed the baseline taxonomy from the corpus (SDKs, modules, Dashboard/Design/
  // Ecosystem). Idempotent and additive — safe to re-run after the IA grows.
  seedDevhubTags: (org: string, version?: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/tags/seed`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, version }),
    }),
  deleteDevhubTag: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/tags/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // ── DevHub release batches (pages that publish together) ─────────────────────
  // Reads are every CMS role — an SME needs to see which release their page is in.
  // Approval and publish are tech-writer/admin, gated server-side.
  getDevhubBatches: (org: string): Promise<DevhubBatch[]> =>
    apiJson(`${API_BASE}/api/devhub/batches?org=${encodeURIComponent(org)}`),
  getDevhubBatch: (id: string): Promise<{ batch: DevhubBatch; pages: DevhubBatchPage[] }> =>
    apiJson(`${API_BASE}/api/devhub/batches/${encodeURIComponent(id)}`),
  // A page's active batch AND its archived history in one read — the editor shows
  // both in one strip, so two round trips would be two loading states.
  getDevhubBatchForPage: (pagePath: string): Promise<{ pagePath: string; active: DevhubBatch | null; history: DevhubBatch[] }> =>
    apiJson(`${API_BASE}/api/devhub/batches/for-page?pagePath=${encodeURIComponent(pagePath)}`),
  // `launchId` links the new batch in the same request; `plannedFromPolaris` says
  // the date is the launch's (the server re-reads it) rather than typed by hand.
  createDevhubBatch: (input: { org: string; name: string; description?: string | null; plannedPublishAt: string | null; launchId?: string | null; plannedFromPolaris?: boolean }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/batches`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  updateDevhubBatch: (id: string, fields: { name?: string; description?: string | null; plannedPublishAt?: string | null }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/batches/${encodeURIComponent(id)}/update`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(fields) }),
  // `move` is the confirmation dialog's answer to "this page is already in another
  // batch" — never a default, so a page can't change release silently.
  addDevhubBatchPages: (id: string, pagePaths: string[], move = false): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/batches/${encodeURIComponent(id)}/pages`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ pagePaths, move }) }),
  removeDevhubBatchPages: (id: string, pagePaths: string[]): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/batches/${encodeURIComponent(id)}/pages/remove`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ pagePaths }) }),
  approveDevhubBatchPage: (id: string, pagePath: string, approved: boolean): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/batches/${encodeURIComponent(id)}/approve`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ pagePath, approved }) }),
  // Without `skipNotReady` a batch holding unready pages is REFUSED with the list,
  // so the writer sees what they would be leaving behind before deciding.
  publishDevhubBatch: (id: string, skipNotReady = false): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/batches/${encodeURIComponent(id)}/publish`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ skipNotReady }) }),
  archiveDevhubBatch: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/batches/${encodeURIComponent(id)}/archive`, { method: "POST", headers: jsonHeaders, body: "{}" }),
  // Batches ↔ Polaris launches, read through the org's `polaris` MCP connection.
  // `configured: false` (a 200) means no usable connection; a 502 means Polaris
  // couldn't be read — the two are rendered differently.
  getDevhubBatchLaunches: (org: string): Promise<PolarisLaunchListing> =>
    apiJson(`${API_BASE}/api/devhub/batches/launches?org=${encodeURIComponent(org)}`),
  getDevhubBatchLaunch: (id: string): Promise<PolarisLaunchView> =>
    apiJson(`${API_BASE}/api/devhub/batches/${encodeURIComponent(id)}/launch`),
  // One launch by id or pasted Polaris URL, with its "Launch readiness (external)" date.
  resolveDevhubBatchLaunch: (org: string, ref: string): Promise<PolarisLaunchResolved> =>
    apiJson(`${API_BASE}/api/devhub/batches/launches/resolve?org=${encodeURIComponent(org)}&ref=${encodeURIComponent(ref)}`),
  // "Use the Polaris date" — the batch's Planned publish follows its launch again.
  followDevhubBatchLaunch: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/batches/${encodeURIComponent(id)}/launch/follow`, { method: "POST", headers: jsonHeaders, body: "{}" }),
  // `launchId: null` unlinks.
  setDevhubBatchLaunch: (id: string, launchId: string | null): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/batches/${encodeURIComponent(id)}/launch`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ launchId }) }),

  // ── DevHub media assets (images/files) ───────────────────────────────────────
  // Org-scoped (not version-scoped, unlike Components). The blob lives in S3;
  // upload/delete/import all degrade to a 501 when the bucket isn't configured.
  getDevhubAssets: (
    org: string,
    opts: { q?: string; page?: number; pageSize?: number; folder?: DevhubAssetFolderKey | null } = {},
  ): Promise<DevhubAssetList> => {
    const q = new URLSearchParams({ org });
    if (opts.q) q.set("q", opts.q);
    if (opts.page) q.set("page", String(opts.page));
    if (opts.pageSize) q.set("pageSize", String(opts.pageSize));
    // Server-side: the list is paginated, so a client-side filter would only
    // narrow the current page and read as a complete answer.
    if (opts.folder) q.set("folder", opts.folder);
    return apiJson(`${API_BASE}/api/devhub/assets?${q.toString()}`);
  },
  /** The folder rail's counts. Not paginated, and unchanged by paging — so it is
   *  fetched per org+search rather than per page. */
  getDevhubAssetFolders: (org: string, search?: string): Promise<DevhubAssetFolderCounts> => {
    const q = new URLSearchParams({ org });
    if (search) q.set("q", search);
    return apiJson(`${API_BASE}/api/devhub/assets/folders?${q.toString()}`);
  },
  getDevhubAsset: (id: string): Promise<DevhubAsset> =>
    apiJson(`${API_BASE}/api/devhub/assets/one?id=${encodeURIComponent(id)}`),
  /** `pagePath` is the page an upload came FROM (the editor's Insert Image /
   *  paste / drop paths). The server seeds usedInPages with it, so the asset is
   *  filed under that page's section immediately instead of appearing unused
   *  until the next rescan. */
  uploadDevhubAsset: (org: string, file: File, alt?: string | null, pagePath?: string): Promise<Response> => {
    const form = new FormData();
    form.set("file", file);
    if (alt) form.set("alt", alt);
    if (pagePath) form.set("pagePath", pagePath);
    return apiFetch(`${API_BASE}/api/devhub/assets/upload?org=${encodeURIComponent(org)}`, { method: "POST", body: form });
  },
  /** The asset behind a URL a page points at — how the editor turns an image it
   *  found in the body into something it can replace. A Response, not parsed
   *  JSON: an unimported hotlink 404s, and that is an answer to show, not an
   *  error to swallow. */
  getDevhubAssetByUrl: (org: string, url: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/assets/one?org=${encodeURIComponent(org)}&url=${encodeURIComponent(url)}`),
  /** What replacing this image WOULD touch: the pages, their owners, and which of
   *  them are outside the caller's scope. Read before a file is chosen — that is
   *  what makes the cross-scope dialog a confirmation rather than a report. */
  getDevhubAssetReplacePlan: (id: string, fromPage?: string | null): Promise<Response> => {
    const q = new URLSearchParams({ id });
    if (fromPage) q.set("fromPage", fromPage);
    return apiFetch(`${API_BASE}/api/devhub/assets/replace/plan?${q.toString()}`);
  },
  /**
   * Replace an image everywhere it is used. `acknowledge` answers the server's
   * 409 for an SME whose replace reaches pages they don't own — the server
   * re-checks the scope either way, so this only ever confirms, never widens.
   */
  replaceDevhubAsset: (opts: {
    id: string;
    file: File;
    name?: string | null;
    alt?: string | null;
    fromPage?: string | null;
    acknowledge?: boolean;
  }): Promise<Response> => {
    const form = new FormData();
    form.set("id", opts.id);
    form.set("file", opts.file);
    if (opts.name) form.set("name", opts.name);
    if (opts.alt) form.set("alt", opts.alt);
    if (opts.fromPage) form.set("fromPage", opts.fromPage);
    if (opts.acknowledge) form.set("acknowledge", "1");
    return apiFetch(`${API_BASE}/api/devhub/assets/replace`, { method: "POST", body: form });
  },
  saveDevhubAsset: (id: string, fields: { alt?: string | null; label?: string | null }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/assets/save`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ id, ...fields }) }),
  /** What deleting this image would do: the pages it is really on, each one's
   *  workflow state, and the references that can't be removed automatically. The
   *  dialog's whole content — the old one showed a count off a list a periodic
   *  rescan re-derives, so the number wasn't the number of pages that changed. */
  getDevhubAssetDeletePlan: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/assets/delete/plan?id=${encodeURIComponent(id)}`),
  /**
   * Delete an asset, taking it off every page that uses it first. `acknowledge`
   * answers the server's 409 for a scoped caller whose delete reaches pages they
   * don't own — the server re-checks the scope either way, so this confirms,
   * never widens.
   */
  deleteDevhubAsset: (id: string, opts?: { acknowledge?: boolean }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/assets/delete`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ id, ...(opts?.acknowledge ? { acknowledge: "1" } : {}) }),
    }),
  rescanDevhubAssets: (org: string): Promise<DevhubAssetRescanResult> =>
    apiJson(`${API_BASE}/api/devhub/assets/rescan`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ org }) }),

  // ── Deployed-site analytics (the DevHub → Analytics tab) ────────────────────
  // Read-only, and NOT from this service's Postgres: the API reads the deployed
  // DevHub site's own DynamoDB counters (see devhub-analytics-source.ts). 403 for
  // any role below tech_writer, which is why the tab is gated to the same tier
  // client-side. `days` is clamped server-side, so an out-of-range value narrows
  // the answer rather than failing.
  getDevhubAnalytics: (org: string, days: number): Promise<DevhubAnalyticsReport> =>
    apiJson(
      `${API_BASE}/api/devhub/analytics?org=${encodeURIComponent(org)}&days=${encodeURIComponent(String(days))}`,
    ),

  // The subjects behind one struggle signal — which pages 404'd, which terms were
  // abandoned. A separate call from the report on purpose: it is the only read
  // whose DynamoDB fan-out is per-signal, so it is paid for when a writer opens a
  // signal to act on it, not on every render of the tab.
  getDevhubFrictionDetail: (
    org: string,
    signal: DevhubFrictionSignal,
    days: number,
  ): Promise<DevhubAnalyticsFrictionDetail> =>
    apiJson(
      `${API_BASE}/api/devhub/analytics/friction/${encodeURIComponent(signal)}?org=${encodeURIComponent(org)}&days=${encodeURIComponent(String(days))}`,
    ),

  // ── Deployed-site status (the DevHub → Status tab) ──────────────────────────
  // Read-only, and NOT from this service's Postgres: the API reads
  // CloudWatch/WAFv2/Route53 for the deployed site (see devhub-status-source.ts).
  // 403 for any role below tech_writer, same gate as Analytics.
  // Publish-to-live, per publish (ENG-57566). Deliberately takes no period: the
  // status tab's picker selects a CloudWatch window, and a publish list cut to
  // "the last 24 hours" would hide the one thing this exists to show — a publish
  // that merged days ago and never arrived.
  getDevhubPublishes: (org: string): Promise<{ targetMs: number; retentionMs?: number; publishes: PublishRow[] }> =>
    apiJson(`${API_BASE}/api/devhub/publishes?org=${encodeURIComponent(org)}`),
  // Removes the TRACKING row only — the page and its PR are untouched. No org:
  // the server checks the role against the row's own org.
  deleteDevhubPublish: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/publishes/${encodeURIComponent(id)}`, { method: "DELETE" }),
  getDevhubStatus: (org: string, period: DevhubStatusPeriod): Promise<DevhubStatusReport> =>
    apiJson(
      `${API_BASE}/api/devhub/status?org=${encodeURIComponent(org)}&period=${encodeURIComponent(period)}`,
    ),

  // The "Recent errors" pane — a SEPARATE call from the report (a Logs Insights
  // query is slower and bills by data scanned, so it must not ride inside every
  // /status response), fired alongside it rather than behind a button. A 502
  // (configured but unreadable) rejects here; a 200 {configured:false} resolves —
  // the pane distinguishes them.
  getDevhubStatusErrors: (
    org: string,
    period: DevhubStatusPeriod,
  ): Promise<DevhubStatusErrorLines> =>
    apiJson(
      `${API_BASE}/api/devhub/status/errors?org=${encodeURIComponent(org)}&period=${encodeURIComponent(period)}`,
    ),

  // ── API Reference ↔ OpenAPI spec sync (the DevHub → API sync tab) ───────────
  // The drift report reconciles the api-reference corpus with the published Omni
  // OpenAPI document. `refresh` bypasses the server's spec cache. A 502
  // (configured but the spec couldn't be read) rejects here; a 200 with
  // `configured: false` resolves — the pane renders those differently on purpose,
  // since "we couldn't look" and "nothing to sync" mean opposite things.
  getDevhubApiSync: (org: string, version: string, refresh = false): Promise<DevhubApiSyncReport> =>
    apiJson(
      `${API_BASE}/api/devhub/openapi/diff?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}${refresh ? "&refresh=1" : ""}`,
    ),
  // Apply the selected endpoints. `keys` are "<METHOD> <path>" identities, not
  // file paths — the server re-derives what each one means against the corpus as
  // it is now, so a stale tab can't publish a page it was looking at earlier.
  syncDevhubApiReference: (input: {
    org: string;
    version: string;
    keys: string[];
    /** Endpoints whose pages should be DELETED — a separate field from `keys`
     *  because deletion is the one thing a sync can't undo, so it never rides
     *  along with an ordinary selection. */
    removeKeys?: string[];
    publishSpec: boolean;
  }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/openapi/sync`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify(input),
    }),
  // What every previous run changed, newest first. Summaries only — the
  // per-endpoint detail is fetched per run, on expand, because a run that fills
  // every stub carries 179 of them.
  getDevhubApiSyncHistory: (org: string, version: string, limit?: number): Promise<{ runs: DevhubApiSyncRunSummary[] }> =>
    apiJson(
      `${API_BASE}/api/devhub/openapi/history?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}${limit ? `&limit=${limit}` : ""}`,
    ),
  getDevhubApiSyncRun: (org: string, id: string): Promise<DevhubApiSyncRun> =>
    apiJson(`${API_BASE}/api/devhub/openapi/history/${encodeURIComponent(id)}?org=${encodeURIComponent(org)}`),

  // ── Brubeck (Incode On-Premise) release documents ─────────────────────────
  // Read-only: the releases in ansible-onprem and the PDF deliverables rendered
  // from them. A 200 with `configured: false` is "no repo token here", distinct
  // from the 502 a configured-but-unreadable repo returns.
  getDevhubBrubeckReleases: (refresh = false): Promise<({ configured: true } & DevhubBrubeckReleases) | { configured: false; error: string }> =>
    apiJson(`${API_BASE}/api/devhub/brubeck/releases${refresh ? "?refresh=1" : ""}`),
  getDevhubBrubeckRelease: (version: string): Promise<DevhubBrubeckReleaseDetail> =>
    apiJson(`${API_BASE}/api/devhub/brubeck/releases/${encodeURIComponent(version)}`),
  // Binary downloads go through apiFetch (not an <a href>) because the request
  // needs the Bearer header; the caller turns the body into a saved file.
  downloadDevhubBrubeckPdf: (version: string, source: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/brubeck/releases/${encodeURIComponent(version)}/pdf?source=${encodeURIComponent(source)}`),
  downloadDevhubBrubeckPackage: (version: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/brubeck/releases/${encodeURIComponent(version)}/package`),
  // Editing: the same stage-then-send flow as SDK releases — every staged edit
  // goes to ansible-onprem as ONE PR against main.
  getDevhubBrubeckSource: (version: string, source: string): Promise<DevhubBrubeckSource> =>
    apiJson(`${API_BASE}/api/devhub/brubeck/releases/${encodeURIComponent(version)}/source?source=${encodeURIComponent(source)}`),
  previewDevhubBrubeckPdf: (version: string, source: string, body: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/brubeck/releases/${encodeURIComponent(version)}/preview`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ source, body }),
    }),
  getDevhubBrubeckReview: (): Promise<DevhubBrubeckReviewState> =>
    apiJson(`${API_BASE}/api/devhub/brubeck/review`),
  stageDevhubBrubeckEdit: (input: { version: string; source: string; body: string; note: string }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/brubeck/edits`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  discardDevhubBrubeckEdit: (path: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/brubeck/edits?path=${encodeURIComponent(path)}`, { method: "DELETE" }),
  sendDevhubBrubeckReview: (): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/brubeck/review`, { method: "POST" }),

  // ── SDK releases ────────────────────────────────────────────────────────────
  // Each SDK (a group under `sdk-reference`) maps to the repo that ships it; its
  // docs are read off a `release/x.y.z` branch of that repo rather than from the
  // corpus. See packages/omni-hub-api/src/devhub/cms-sdk.ts.

  getDevhubSdks: (org: string): Promise<DevhubSdkSummary[]> =>
    apiJson(`${API_BASE}/api/devhub/sdk?org=${encodeURIComponent(org)}`),
  // Discovery reaches GitHub, so this is the call that 501s on a checkout with no
  // publish token — the pane reports that as "not configured", not as an error.
  getDevhubSdkReleases: (org: string, group: string): Promise<DevhubSdkBranch[]> =>
    apiJson(`${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/releases?org=${encodeURIComponent(org)}`),
  // With neither `file` nor `slug`, the release branch's page list; with either,
  // that page's markdown. `slug` addresses a page the way DevHub does, so the
  // reader can ask for the release's version of the page it is showing without
  // reconstructing a repo path.
  //
  // `tree` picks WHICH half of the repo is read — the SDK's docs (default) or
  // `docs/_devhub/releases`, which holds the per-version records and the SDK's
  // release-notes page. The server resolves it to a dir from an allowlist; no
  // caller ever names a repo path.
  //
  // A single-page read also carries `published`: the SAME page as the corpus has
  // it, or null when the corpus has no such page. It travels with the branch body
  // because saying WHICH sections of a changed page changed needs both, and the
  // server already holds the corpus copy on local disk.
  getDevhubSdkContent: (
    org: string, group: string, branch: string,
    opts: { file?: string; slug?: string; tree?: DevhubSdkTree; version?: string } = {},
  ): Promise<{
    branch: string; contentDir?: string; files?: DevhubSdkContentFile[];
    file?: string; path?: string; body?: string;
    /** the file's blob sha on the branch — what a staged edit's base is compared
     *  with to see whether the branch has moved underneath it */
    sha?: string;
    published?: string | null; publishedPath?: string | null;
    /** the page as the OPEN review PR has it, with the PR that holds it — the
     *  version the writer submitted, which the release branch will not carry until
     *  somebody on the SDK team merges. Null in the ordinary case (ENG-55485) */
    review?: { body: string; number: number; url: string } | null;
    /** set when that could not be read: the body on screen may then be older than
     *  what was submitted, which must not look like "nothing in review" */
    reviewError?: string | null;
  }> =>
    apiJson(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/content?org=${encodeURIComponent(org)}&branch=${encodeURIComponent(branch)}${
        opts.file ? `&file=${encodeURIComponent(opts.file)}` : ""
      }${opts.slug ? `&slug=${encodeURIComponent(opts.slug)}` : ""}${opts.tree ? `&tree=${encodeURIComponent(opts.tree)}` : ""}${
        opts.version ? `&version=${encodeURIComponent(opts.version)}` : ""
      }`,
    ),
  // The body a staged edit was made against — the third side of the three-way
  // merge that pulls the SDK team's later edits in (ENG-55485). Its own call
  // rather than a field on the content read, which every page open makes: the
  // base is needed only when a writer actually asks to pull.
  //
  // `base: null` with a `reason` is a normal answer, not a failure — an edit
  // staged before Omni Hub recorded its base has no third side, and the caller
  // falls back to the two-way choice it already offers.
  getDevhubSdkPullBase: (
    org: string, group: string, branch: string, file: string, tree: DevhubSdkTree,
  ): Promise<{ base: string | null; baseSha?: string; reason?: string }> =>
    apiJson(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/pull-base?org=${encodeURIComponent(org)}&branch=${encodeURIComponent(branch)}&file=${encodeURIComponent(file)}&tree=${encodeURIComponent(tree)}`,
    ),
  // The release's content as PAGES — titled, grouped and ordered the way the corpus
  // is, so the SDKs pane renders a release with the Content tab's menu instead of a
  // list of repo paths. Costlier than the file listing above (it reads each page's
  // page.yml), hence a separate call rather than a fatter one.
  //
  // `snapshot` is true when the branch is the PUBLISHED release and the SDK team has
  // deleted it: the pages then come from the corpus, i.e. what was published, and
  // the release is read-only (ENG-57993, QA). `version` picks the corpus version a
  // snapshot is read from; a live branch ignores it.
  getDevhubSdkPages: (
    org: string, group: string, branch: string, tree?: DevhubSdkTree, version?: string,
  ): Promise<{ branch: string; repo: string; contentDir: string; pages: DevhubSdkPage[]; snapshot?: boolean }> =>
    apiJson(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/pages?org=${encodeURIComponent(org)}&branch=${encodeURIComponent(branch)}${
        tree ? `&tree=${encodeURIComponent(tree)}` : ""
      }${version ? `&version=${encodeURIComponent(version)}` : ""}`,
    ),
  getDevhubSdkReleaseMeta: (
    org: string, group: string, branch: string, version: string,
  ): Promise<{ releaseDate: string | null; bomVersion: string | null; status: string | null }> =>
    apiJson(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/release-meta?org=${encodeURIComponent(org)}&branch=${encodeURIComponent(branch)}&version=${encodeURIComponent(version)}`,
    ),
  getDevhubSdkReleaseFolders: (org: string, group: string, branch: string): Promise<DevhubSdkReleaseFolder[]> =>
    apiJson(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/release-folders?org=${encodeURIComponent(org)}&branch=${encodeURIComponent(branch)}`,
    ),
  // The CMS-opened PRs against a release branch, per content file: the link a
  // writer answers review comments on, and whether it has landed. Live from
  // GitHub, so it reflects a merge done on the PR page itself.
  getDevhubSdkPrs: (org: string, group: string, branch: string, tree?: DevhubSdkTree): Promise<DevhubSdkContentPr[]> =>
    apiJson(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/prs?org=${encodeURIComponent(org)}&branch=${encodeURIComponent(branch)}${
        tree ? `&tree=${encodeURIComponent(tree)}` : ""
      }`,
    ),
  // How this release differs from what the site serves — what the menu colours
  // green (new) / blue (changed) / red (deleted), and the same comparison the
  // publish plan runs, so a colour and the plan cannot disagree.
  getDevhubSdkDiff: (
    org: string, group: string, branch: string, version: string, tree?: DevhubSdkTree,
  ): Promise<DevhubSdkDiff> =>
    apiJson(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/diff?org=${encodeURIComponent(org)}&branch=${encodeURIComponent(branch)}&version=${encodeURIComponent(version)}${
        tree ? `&tree=${encodeURIComponent(tree)}` : ""
      }`,
    ),

  // What has landed on a PUBLISHED release's branch since it was published — the
  // commits, and which of this SDK's documentation files they touched. Answers
  // `update: null` with a reason (rather than an error) when the question does not
  // apply: an SDK nobody has published, or a branch that is not the published one.
  getDevhubSdkBranchUpdate: (
    org: string, group: string, branch: string,
  ): Promise<{ update: DevhubSdkBranchUpdate | null; reason: string | null }> =>
    apiJson(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/branch-update?org=${encodeURIComponent(org)}&branch=${encodeURIComponent(branch)}`,
    ),

  // ── Review edits ────────────────────────────────────────────────────────────
  // An edit is STAGED here, not committed: the whole set is then sent as one PR
  // against the release branch, so the SDK team reviews one diff rather than one
  // per page. `note` is required — it becomes this page's bullet in that PR.

  stageDevhubSdkEdit: (input: {
    org: string; group: string; branch: string; file: string; body: string; note: string; tree?: DevhubSdkTree;
  }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/sdk/${encodeURIComponent(input.group)}/edits?org=${encodeURIComponent(input.org)}`, {
      method: "POST", headers: jsonHeaders,
      body: JSON.stringify({ branch: input.branch, file: input.file, body: input.body, note: input.note, tree: input.tree }),
    }),
  getDevhubSdkEdits: (org: string, group: string, branch?: string): Promise<DevhubSdkEdit[]> =>
    apiJson(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/edits?org=${encodeURIComponent(org)}${
        branch ? `&branch=${encodeURIComponent(branch)}` : ""
      }`,
    ),
  discardDevhubSdkEdit: (
    org: string, group: string, branch: string, file: string, tree?: DevhubSdkTree,
  ): Promise<Response> =>
    apiFetch(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/edits?org=${encodeURIComponent(org)}&branch=${encodeURIComponent(branch)}&file=${encodeURIComponent(file)}${
        tree ? `&tree=${encodeURIComponent(tree)}` : ""
      }`,
      { method: "DELETE" },
    ),
  // Ignore / restore a release branch the repo carries but that is not a release —
  // a placeholder that would otherwise rank as "latest" (ENG-57993). Both answer
  // with the updated summary under `sdk`.
  ignoreDevhubSdkBranch: (org: string, group: string, branch: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/ignored-branches?org=${encodeURIComponent(org)}`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ branch }),
    }),
  unignoreDevhubSdkBranch: (org: string, group: string, branch: string): Promise<Response> =>
    apiFetch(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/ignored-branches?org=${encodeURIComponent(org)}&branch=${encodeURIComponent(branch)}`,
      { method: "DELETE" },
    ),
  // The one review PR for a release, as GitHub has it — open, merged or absent.
  getDevhubSdkReviewPr: (org: string, group: string, branch: string): Promise<{ pr: DevhubSdkReviewPr | null }> =>
    apiJson(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/review-pr?org=${encodeURIComponent(org)}&branch=${encodeURIComponent(branch)}`,
    ),
  sendDevhubSdkReviewPr: (org: string, group: string, branch: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/review-pr?org=${encodeURIComponent(org)}`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ branch }),
    }),

  /** Bring a closed, unmerged review PR's edits back into the send set
   *  (ENG-55495) — read off the PR's head commit, staged like any other edit. */
  restageDevhubSdkReviewPr: (org: string, group: string, branch: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/review-pr/restage?org=${encodeURIComponent(org)}`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ branch }),
    }),

  // ── Publishing a release into the corpus ────────────────────────────────────
  // The same gate the POST re-runs, so the button and the action cannot disagree.
  checkDevhubSdkPublish: (
    org: string, group: string, branch: string, version: string,
  ): Promise<{
    blockers: DevhubSdkPublishBlocker[];
    plan: DevhubSdkPublishPlan | null;
    publishable: boolean;
    /** pages the site already serves whose draft row still reads Draft or SME
     *  review — what "Fix page status" would settle (ENG-55485). */
    unsettled: string[];
    /** what the Release Notes feed prompt needs (ENG-62099); null when unreadable */
    releaseFeed: DevhubSdkReleaseFeedInfo | null;
  }> =>
    apiJson(
      `${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/publish-check?org=${encodeURIComponent(org)}&branch=${encodeURIComponent(branch)}&version=${encodeURIComponent(version)}`,
    ),
  publishDevhubSdkRelease: (
    org: string, group: string, version: string, branch: string,
    // The ONE overridable blocker: a release that would revert a page published
    // off-cycle. Sent per request and never remembered, so a stale tab cannot carry
    // an override into a later attempt.
    opts: { acknowledgeRegression?: boolean; releaseFeed?: DevhubSdkReleaseFeedAnswer } = {},
  ): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/publish?org=${encodeURIComponent(org)}`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({
        version, branch,
        ...(opts.acknowledgeRegression ? { acknowledgeRegression: true } : {}),
        ...(opts.releaseFeed ? { releaseFeed: opts.releaseFeed } : {}),
      }),
    }),

  // Off-cycle publishes ONE page: `develop` carries work in progress, so making the
  // corpus equal it would ship unreleased documentation and delete pages develop has
  // not caught up with. See the API's /sdk/:group/publish-page.
  publishDevhubSdkPage: (input: {
    org: string; group: string; version: string; branch: string; file: string; tree: DevhubSdkTree;
    releaseFeed?: DevhubSdkReleaseFeedAnswer;
  }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/sdk/${encodeURIComponent(input.group)}/publish-page?org=${encodeURIComponent(input.org)}`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({
        version: input.version, branch: input.branch, file: input.file, tree: input.tree,
        ...(input.releaseFeed ? { releaseFeed: input.releaseFeed } : {}),
      }),
    }),

  // Settle the draft rows of pages this SDK has already published — the repair for
  // rows made before an SDK publish settled them (ENG-55485). Idempotent, and it
  // only touches a draft whose body is exactly the page the site serves.
  reconcileDevhubSdkDrafts: (org: string, group: string, version: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/sdk/${encodeURIComponent(group)}/reconcile-drafts?org=${encodeURIComponent(org)}`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ version }),
    }),

  // ── Notifications (the top-bar bell) ────────────────────────────────────────
  // The caller's OWN notifications (server-scoped by OIDC sub); mark one or all
  // read. Fired across the app — DevHub review, session share/activity, task-done
  // (see core/notify.ts + devhub-notify.ts).
  getNotifications: (bucket?: NotificationBucket): Promise<HubNotification[]> =>
    apiJson(`${API_BASE}/api/notifications${bucket ? `?bucket=${bucket}` : ""}`),
  getNotificationCounts: (): Promise<Record<NotificationBucket, number>> =>
    apiJson(`${API_BASE}/api/notifications/counts`),
  markNotificationRead: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/notifications/${encodeURIComponent(id)}/read`, { method: "POST" }),
  markAllNotificationsRead: (): Promise<Response> =>
    apiFetch(`${API_BASE}/api/notifications/read-all`, { method: "POST" }),
  // One call for every action and any number of rows — see the route's comment:
  // a separate single-row endpoint is a second implementation of the same five
  // verbs, and the one that drifts is the one nobody exercises.
  applyNotificationAction: (
    ids: string[],
    action: NotificationAction,
  ): Promise<Response> =>
    apiFetch(`${API_BASE}/api/notifications/actions`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ ids, action }),
    }),

  // ── DevHub CMS admin settings (users/roles + config status) ────────────────
  // A CMS role is per ORG, so every one of these carries the org whose roster is
  // being read or written. The routes require it (400 without) rather than
  // defaulting: these four are the only DevHub routes that WRITE a role row, and
  // the old default sent AID's role grants into Incode's table while reporting
  // success. Passing it is therefore not an optimization — it is which tenant the
  // request is about.
  getDevhubUsers: (org: string): Promise<DevhubUserRole[]> =>
    apiJson(`${API_BASE}/api/devhub/users?org=${encodeURIComponent(org)}`),
  /** The people ATTACHED to this org — a role here, an org_members grant naming it,
   *  or an agent registered in it — merged with the role each holds HERE. There is
   *  no parameter that widens it: somebody outside the org is onboarded by an Omni
   *  Hub admin attaching them to it in Settings → Users, then given a role here. */
  getDevhubDirectory: (org: string): Promise<DevhubDirectoryEntry[]> =>
    apiJson(`${API_BASE}/api/devhub/directory?org=${encodeURIComponent(org)}`),
  setDevhubUser: (input: { org: string; sub: string; email: string; name?: string | null; role: string; team?: string | null; tags?: string[] }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/users`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  deleteDevhubUser: (sub: string, org: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/users/delete`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ sub, org }) }),
  // ONE org's configuration — the server requires the org.
  getDevhubConfig: (org: string): Promise<DevhubConfigStatus> =>
    apiJson(`${API_BASE}/api/devhub/config?org=${encodeURIComponent(org)}`),

  // ── DevHub teams (SMEs, one of them the lead, and the scope they inherit) ──
  getDevhubTeams: (org: string): Promise<DevhubTeamSummary[]> =>
    apiJson(`${API_BASE}/api/devhub/teams?org=${encodeURIComponent(org)}`),
  /** The caller's own teams — a list, since somebody may lead one and sit on
   *  another. Ungated beyond auth: it says something about you, never about the
   *  roster. */
  getMyDevhubTeams: (org: string): Promise<DevhubTeamSummary[]> =>
    apiJson(`${API_BASE}/api/devhub/teams/mine?org=${encodeURIComponent(org)}`),
  /** SMEs on no team — the set a roster ADD may draw from. Served rather than
   *  filtered client-side so the picker cannot offer somebody the server's rule
   *  would refuse, and because a team's LEAD cannot read the directory at all
   *  (ENG-54991). */
  getDevhubTeamCandidates: (org: string): Promise<{ sub: string; email: string }[]> =>
    apiJson(`${API_BASE}/api/devhub/teams/candidates?org=${encodeURIComponent(org)}`),
  createDevhubTeam: (input: { org: string; slug: string; name: string }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/teams`, { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) }),
  /** `leadSub` must already be one of the team's members — the server refuses it
   *  otherwise rather than quietly ignoring it. */
  saveDevhubTeam: (id: string, fields: { name?: string; sections?: string[]; tagIds?: string[]; leadSub?: string | null }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/teams/save`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ id, ...fields }) }),
  setDevhubTeamMember: (id: string, sub: string, action: "add" | "remove"): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/teams/members`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ id, sub, action }) }),
  deleteDevhubTeam: (id: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/teams/delete`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ id }) }),

  // ── DevHub repository mapping (section → owner repo) ───────────────────────
  getDevhubMappings: (org: string): Promise<DevhubSectionOwner[]> =>
    apiJson(`${API_BASE}/api/devhub/mappings?org=${encodeURIComponent(org)}`),
  setDevhubMapping: (org: string, section: string, repo: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/mappings`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, section, repo }),
    }),
  resetDevhubMapping: (org: string, section: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/mappings/reset`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, section }),
    }),
  // The section's target-branch override — the branch the CMS publish flow opens
  // PRs against, instead of the repo's actual default branch. Kept separate from
  // the repo override so resetting one doesn't clobber the other.
  setDevhubMappingBranch: (org: string, section: string, branch: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/mappings/branch`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, section, branch }),
    }),
  resetDevhubMappingBranch: (org: string, section: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/mappings/branch/reset`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, section }),
    }),
  setDevhubSectionDescription: (org: string, section: string, description: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/mappings/description`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, section, description }),
    }),

  // ── DevHub legacy-URL redirects (Settings → Redirects) ─────────────────────
  // Pre-cutover mapping from the live public docs site (developer.incode.com) to
  // the new v1.1 corpus, with an org-wide fallback for unmapped URLs. The map is
  // curated here, then publishDevhubRedirects ships it to the standalone DevHub
  // site (a PR into the KB repo) where the build enforces the redirects.
  getDevhubRedirects: (org: string): Promise<{ rows: DevhubRedirect[]; settings: DevhubRedirectSettings | null; stats: { total: number; mapped: number; unmapped: number } }> =>
    apiJson(`${API_BASE}/api/devhub/redirects?org=${encodeURIComponent(org)}`),
  setDevhubRedirect: (org: string, legacyPath: string, target: DevhubRedirectTarget): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/redirects`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, legacyPath, target }),
    }),
  bulkSetDevhubRedirects: (org: string, legacyPaths: string[], target: DevhubRedirectTarget): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/redirects/bulk`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, legacyPaths, target }),
    }),
  deleteDevhubRedirect: (org: string, legacyPath: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/redirects/delete`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, legacyPath }),
    }),
  setDevhubRedirectFallback: (org: string, fallback: Exclude<DevhubRedirectTarget, { type: "none" }>): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/redirects/fallback`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, fallback }),
    }),
  // (Re-)populate from the checked-in developer.incode.com snapshot — only fills
  // legacy paths with no row yet, never overwrites a human edit.
  seedDevhubRedirects: (org: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/redirects/seed`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org }),
    }),
  // Ship the curated map to the standalone DevHub site: opens (auto-merges) a PR
  // that writes a resolved cms-redirects.json into the KB repo, which the site
  // build consumes. Takes effect on the next site deploy.
  publishDevhubRedirects: (org: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/redirects/publish`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org }),
    }),

  // ── DevHub tab/menu reordering (drag-and-drop) ─────────────────────────────
  getDevhubOrder: (org: string, version: string): Promise<DevhubOrder> =>
    apiJson(`${API_BASE}/api/devhub/order?org=${encodeURIComponent(org)}&version=${encodeURIComponent(version)}`),
  // `nesting` stages a re-nest (page path → its new nav chain, null to drop the
  // staged move); it rides the same route as the order because they publish
  // together — see DevhubNesting.
  setDevhubOrder: (org: string, version: string, body: { sections?: string[]; pages?: Record<string, number>; tabs?: string[]; nesting?: Record<string, DevhubNesting | null>; scope?: string[] }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/order`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, version, ...body }),
    }),
  // Deliver the SME review requests that were never sent — see the CMS's
  // devhub-review-reconcile.ts. An action, not a migration: it can be re-run,
  // reports what it did, and skips every draft already accounted for.
  reconcileDevhubReviewRequests: (org: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/drafts/reconcile-review-requests`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org }),
    }),
  resetDevhubOrder: (org: string, version: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/order/reset`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, version }),
    }),
  // Put the staged menu change into a batch (ENG-62129) — it publishes with the
  // batch, after the batch's pages. `batchId` / `createNew` answers a prompt.
  stageDevhubOrder: (org: string, version: string, answer: { batchId?: string; createNew?: boolean } = {}): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/order/stage`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify({ org, version, ...answer }),
    }),

  // Per-page repo/file mapping (the page-bar, read-only): effective repo (section
  // mapping) + derived docs/_devhub file.
  getDevhubPageMapping: (path: string): Promise<DevhubPageBar> =>
    apiJson(`${API_BASE}/api/devhub/page-mapping?path=${encodeURIComponent(path)}`),

  // Which repo the corpus lives in, and at which branch. Server-resolved from
  // DEVHUB_CONTENT_TARGET — never inferred here, or it goes stale at the cutover.
  getDevhubCorpus: (): Promise<DevhubCorpusInfo> =>
    apiJson(`${API_BASE}/api/devhub/corpus`),

  // ── DevHub page assist (ask the repo agent to author/improve via devhub_docs) ─
  getDevhubAssists: (page: string): Promise<DevhubAssist[]> =>
    apiJson(`${API_BASE}/api/devhub/assist?page=${encodeURIComponent(page)}`),
  requestDevhubAssist: (input: { path: string; mode: "generate" | "improve"; draftId?: string | null; instruction?: string; agentIds?: string[] }): Promise<Response> =>
    apiFetch(`${API_BASE}/api/devhub/assist`, {
      method: "POST", headers: jsonHeaders, body: JSON.stringify(input),
    }),

  // ── Agents — per-repo .agents/ summaries grouped by org ────────────────────
  getAgentRepos: (org: string): Promise<{ org: string; total: number; repos: AgentRepoEntry[] }> =>
    apiJson(`${API_BASE}/api/agents/repos?org=${encodeURIComponent(org)}`),
  getAgentSummary: (org: string, repo: string, category: string): Promise<Response> =>
    apiFetch(
      `${API_BASE}/api/agents/summary/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/${encodeURIComponent(category)}`
    ),
  getAgentSpec: (org: string, repo: string): Promise<Response> =>
    apiFetch(`${API_BASE}/api/agents/spec/${encodeURIComponent(org)}/${encodeURIComponent(repo)}`),
  // Live .agents/ tree for a repo (read via the mirror symlink — no duplication).
  getAgentTree: (org: string, repo: string): Promise<AgentTree> =>
    apiJson(`${API_BASE}/api/agents/tree/${encodeURIComponent(org)}/${encodeURIComponent(repo)}`),
  getAgentFile: (org: string, repo: string, relPath: string): Promise<Response> =>
    apiFetch(
      `${API_BASE}/api/agents/file/${encodeURIComponent(org)}/${encodeURIComponent(repo)}/` +
        relPath.split("/").map(encodeURIComponent).join("/")
    ),

  // ── Agents Common — shared config files under <category>/common ───────────
  getCommonFiles: (category: string): Promise<{ category: string; files: CommonFile[] }> =>
    apiJson(`${API_BASE}/api/common/${encodeURIComponent(category)}`),
  getCommonFile: (category: string, relPath: string): Promise<Response> =>
    apiFetch(
      `${API_BASE}/api/common/${encodeURIComponent(category)}/file/` +
        relPath.split("/").map(encodeURIComponent).join("/")
    ),

  // ── System Skills ──────────────────────────────────────────────────────────
  getSkills: (): Promise<SkillSummary[]> =>
    apiJson(`${API_BASE}/api/skills`),
  getSkill: (slug: string): Promise<SkillDetail> =>
    apiJson(`${API_BASE}/api/skills/${encodeURIComponent(slug)}`),
  getSkillFile: (slug: string, relPath: string): Promise<Response> =>
    apiFetch(
      `${API_BASE}/api/skills/${encodeURIComponent(slug)}/file/` +
        relPath.split("/").map(encodeURIComponent).join("/")
    ),
};
